<script setup>
/* eslint-disable no-alert */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  ContractTemplatesAPI,
  ContractsAPI,
} from 'dashboard/api/talkviaContracts';
import TalkviaCreditProductsAPI from 'dashboard/api/talkviaCreditProducts';
import TalkviaInventoryProductsAPI from 'dashboard/api/talkviaInventoryProducts';
import TalkviaInventoryProductGroupsAPI from 'dashboard/api/talkviaInventoryProductGroups';
import ContactsAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import ContractSendDialog from './ContractSendDialog.vue';

const copy = {
  title: 'Contratos',
  description:
    'Crie modelos personalizados, gere contratos para clientes e acompanhe assinaturas.',
  templates: 'Modelos de contrato',
  contracts: 'Contratos gerados',
  newTemplate: 'Novo modelo',
  saveTemplate: 'Salvar modelo',
  updateTemplate: 'Atualizar modelo',
  generateContract: 'Gerar contrato',
  addProduct: 'Adicionar produto',
  sendLink: 'Enviar link',
  sendToContact: 'Enviar contrato para contato',
  sendQuote: 'Enviar orçamento',
  openConversation: 'Abrir conversa',
  openLink: 'Abrir',
  delete: 'Excluir',
  deleteTitle: 'Excluir contrato',
  deleteDescription:
    'Confirme para excluir este contrato. Esta ação não poderá ser desfeita.',
  cancel: 'Cancelar',
  confirmDelete: 'Excluir contrato',
  statusUpdated: 'Status do contrato atualizado.',
  pendingReopened:
    'Contrato reaberto para assinatura. Um novo link foi gerado com validade de 3 dias.',
  pending: 'Pendentes',
  signed: 'Assinados',
  expired: 'Expirados',
  cancelled: 'Cancelados',
  awaiting_delivery: 'Aguardando entrega',
  completed: 'Concluído',
  all: 'Todos',
  searchPlaceholder: 'Buscar por telefone, CPF/CNPJ ou nome',
  bodyHint: 'Clique em um parâmetro para inserir no editor.',
  downloadReceipt: 'Baixar comprovante PDF',
  viewDocuments: 'Ver anexos',
  documentsTitle: 'Anexos do contrato',
  noDocuments: 'Nenhum anexo encontrado.',
  downloadDocument: 'Baixar',
  requiredDocuments: 'Documentos para assinatura',
  customDocument: 'Outro documento',
  invoiceTotal: 'Total final',
  contactSearch: 'Contato cadastrado',
  edit: 'Editar',
  close: 'Fechar',
  manageTemplates: 'Modelos',
  contractTemplates: 'Contratos',
  quoteTemplates: 'Orçamentos',
  newQuoteTemplate: 'Novo orçamento',
  addPaymentMethod: 'Adicionar forma de pagamento',
  paymentMethodSaved: 'Forma de pagamento salva.',
  dashboard: 'Dashboard',
  editContract: 'Editar contrato',
  editContractTitle: 'Editar contrato',
  saveContract: 'Salvar contrato',
  contractUpdated: 'Contrato atualizado.',
  contractUpdateFailed: 'Não foi possível atualizar o contrato.',
  contractEditLocked:
    'Contratos assinados ou concluídos não podem ser editados.',
  deliveryDate: 'Data de entrega',
  pickupDate: 'Data de retirada',
  deliveryRange: 'Entrega / retirada',
  addressSection: 'Endereço do contato',
  fullAddress: 'Endereço completo',
  addressMergeHint: 'Use {{endereco_completo}} para mesclar tudo no modelo.',
  useContactAddress: 'Usar endereço cadastrado no contato',
  customContractAddress: 'Usar outro endereço neste contrato',
  lookupZipCode: 'Consultar CEP',
  zipCodeNotFound: 'CEP não encontrado.',
};

const CUSTOM_SERVICE_PRODUCT_TYPE = 'TalkviaCustomService';
const CUSTOM_SERVICE_VALUE = 'custom-service';

const DEFAULT_PAYMENT_METHODS = [
  'Pix',
  'Boleto bancário',
  'Cartão de crédito',
  'Cartão de débito',
  'Dinheiro',
  'Transferência',
];

const PARAMETER_OPTIONS = [
  '{{nome}}',
  '{{cpf}}',
  '{{cnpj}}',
  '{{documento}}',
  '{{tipo_documento}}',
  '{{telefone}}',
  '{{data_geracao}}',
  '{{data_assinatura}}',
  '{{data_entrega}}',
  '{{data_retirada}}',
  '{{cep}}',
  '{{endereco}}',
  '{{numero}}',
  '{{numero_endereco}}',
  '{{numero_orcamento}}',
  '{{complemento}}',
  '{{bairro}}',
  '{{cidade}}',
  '{{uf}}',
  '{{endereco_completo}}',
  '{{produto}}',
  '{{produtos}}',
  '{{produtos_lista}}',
  '{{produtos_tabela}}',
  '{{total}}',
  '{{total_produtos}}',
  '{{valor_total_produtos}}',
  '{{forma_pagamento}}',
];

const DEFAULT_DOCUMENTS = ['CPF/CNH', 'Comprovante Endereço', 'RG'];
const FIELD_LABEL_CLASS =
  'mb-0 grid gap-1.5 text-sm font-medium text-n-slate-12';
const FIELD_CONTROL_CLASS =
  '!mb-0 h-10 w-full rounded-lg !border !border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 shadow-sm transition placeholder:text-n-slate-10 hover:bg-n-alpha-2 focus:!border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand disabled:cursor-not-allowed disabled:opacity-50';
const INPUT_CLASS = `reset-base ${FIELD_CONTROL_CLASS}`;
const DATE_CONTROL_CLASS = `${FIELD_CONTROL_CLASS} flex items-center justify-between gap-3 text-left`;
const DATE_NATIVE_INPUT_CLASS =
  'pointer-events-none absolute inset-0 h-full w-full opacity-0';
const SELECT_BUTTON_CLASS =
  '!h-10 w-full !justify-between !rounded-lg !border !border-n-weak !bg-n-solid-1 !px-3 !text-n-slate-12 shadow-sm hover:!bg-n-alpha-2 focus:!border-n-brand focus:!ring-1 focus:!ring-n-brand';
const SELECT_BUTTON_OPEN_CLASS =
  '!border-n-brand !bg-n-alpha-2 ring-1 ring-n-brand';

const EDIT_FIELD_LABEL_CLASS =
  'mb-0 grid gap-1 text-xs font-semibold text-n-slate-11';
const EDIT_INPUT_CLASS = `reset-base ${FIELD_CONTROL_CLASS} !h-9 !rounded-md !px-2.5 !text-sm`;
const EDIT_SELECT_BUTTON_CLASS = `${SELECT_BUTTON_CLASS} !h-9 !rounded-md !px-2.5 !text-sm`;

const statusOptions = [
  { label: copy.all, value: '' },
  { label: copy.pending, value: 'pending' },
  { label: copy.signed, value: 'signed' },
  { label: copy.awaiting_delivery, value: 'awaiting_delivery' },
  { label: copy.completed, value: 'completed' },
  { label: copy.expired, value: 'expired' },
  { label: copy.cancelled, value: 'cancelled' },
];
const contractStatusOptions = statusOptions.filter(option => option.value);
const dateRangeFilters = [
  {
    key: 'delivery',
    label: 'Entrega',
    startKey: 'start_at',
    endKey: 'end_at',
  },
  {
    key: 'pickup',
    label: 'Retirada',
    startKey: 'pickup_start_at',
    endKey: 'pickup_end_at',
  },
];
const dateRangePresets = [
  { key: 'last7', label: 'Últimos 7 dias' },
  { key: 'last30', label: 'Últimos 30 dias' },
  { key: 'last3m', label: 'Últimos 3 meses' },
  { key: 'last6m', label: 'Últimos 6 meses' },
  { key: 'lastYear', label: 'Ano passado' },
  { key: 'thisWeek', label: 'Esta semana' },
  { key: 'thisMonth', label: 'Este mês' },
  { key: 'custom', label: 'Intervalo personalizado' },
];
const weekDays = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];

const { currentAccount, updateAccount } = useAccount();

const templates = ref([]);
const contracts = ref([]);
const allContracts = ref([]);
const summary = ref({});
const creditProducts = ref([]);
const inventoryProducts = ref([]);
const inventoryProductGroups = ref([]);
const isLoading = ref(false);
const selectedTemplateId = ref('');
const filters = reactive({
  status: '',
  q: '',
  start_at: '',
  end_at: '',
  pickup_start_at: '',
  pickup_end_at: '',
});
const activeDateRangeFilter = ref('');
const dateRangeFilterRefs = ref({});
const formSelectMenuRefs = ref({});
const dateRangeDraft = reactive({
  start: '',
  end: '',
  preset: 'custom',
  month: '',
});
const contactSearch = ref('');
const contactResults = ref([]);
const isSearchingContacts = ref(false);
const contractSendDialogRef = ref(null);
const deleteContractDialogRef = ref(null);
const showDocumentsModal = ref(false);
const selectedDocuments = ref([]);
const selectedDocumentsName = ref('');
const contractPendingDelete = ref(null);
const isDeletingContract = ref(false);
const updatingStatusContractId = ref(null);
const openContractStatusMenuId = ref(null);
const openContractActionsId = ref(null);
const showContractStatusFilter = ref(false);
const openFormSelectMenu = ref('');
const router = useRouter();
const route = useRoute();

// Stock by date
const stockDate = ref(new Date().toISOString().slice(0, 10));
const stockDateMonth = ref(stockDate.value);
const showStockDatePicker = ref(false);
const stockDatePickerRef = ref(null);
const stockByDateResults = ref([]);
const isLoadingStock = ref(false);

const showGenerateContractModal = ref(false);
const contractRangeType = ref(DATE_RANGE_TYPES.CUSTOM_RANGE);
const contractEditRangeType = ref(DATE_RANGE_TYPES.CUSTOM_RANGE);

// Template modal
const showTemplateModal = ref(false);
const templateModalKind = ref('contract');

// Dashboard modal
const showDashboard = ref(false);
const isDashboardExpanded = ref(false);

const useContactAddress = ref(true);
const selectedContactAddress = ref({});
const selectedContact = ref(null);
const selectedDocumentType = ref('');
const isAddressSectionCollapsed = ref(false);
const isFetchingContractZipCode = ref(false);
const lastContractZipCodeLookup = ref('');
const isFetchingContractEditZipCode = ref(false);
const lastContractEditZipCodeLookup = ref('');

// Payment method management
const customPaymentMethods = ref([]);
const showAddPaymentInput = ref(false);
const newPaymentMethodInput = ref('');
const isSavingPaymentMethod = ref(false);

const allPaymentMethods = computed(() => [
  ...DEFAULT_PAYMENT_METHODS,
  ...customPaymentMethods.value,
]);

const templateForm = reactive({
  id: null,
  name: '',
  body: '',
  active: true,
  require_selfie: true,
  metadata: {
    required_documents: [...DEFAULT_DOCUMENTS],
  },
});

const customDocumentName = ref('');

const contractForm = reactive({
  talkvia_contract_template_id: '',
  contact_id: '',
  recipient_name: '',
  recipient_cpf: '',
  recipient_phone: '',
  recipient_zip_code: '',
  recipient_address: '',
  recipient_address_number: '',
  recipient_address_complement: '',
  recipient_neighborhood: '',
  recipient_city: '',
  recipient_state: '',
  recipient_address_source: 'contact',
  scheduled_at: '',
  pickup_at: '',
  payment_method: '',
});

const showContractEditModal = ref(false);
const editingContract = ref(null);
const isSavingContractEdit = ref(false);
const contractEditForm = reactive({
  recipient_name: '',
  recipient_cpf: '',
  recipient_phone: '',
  recipient_zip_code: '',
  recipient_address: '',
  recipient_address_number: '',
  recipient_address_complement: '',
  recipient_neighborhood: '',
  recipient_city: '',
  recipient_state: '',
  recipient_address_source: 'contract',
  scheduled_at: '',
  pickup_at: '',
  payment_method: '',
});

const invoiceItemForm = reactive({
  product_value: '',
  custom_name: '',
  quantity: 1,
  unit_price: '',
});

const invoiceItems = ref([]);

const contractEditInvoiceItemForm = reactive({
  product_value: '',
  custom_name: '',
  quantity: 1,
  unit_price: '',
});

const contractEditInvoiceItems = ref([]);

const productPrice = (...values) => {
  const rawValue = values.find(
    value => value !== undefined && value !== null && value !== ''
  );
  if (rawValue === undefined) return 0;
  if (typeof rawValue === 'number') return rawValue;

  const normalizedValue = String(rawValue).trim();
  if (normalizedValue.includes(',')) {
    return (
      Number(
        normalizedValue
          .replace(/\./g, '')
          .replace(',', '.')
          .replace(/[^\d.-]/g, '')
      ) || 0
    );
  }

  return Number(normalizedValue.replace(/[^\d.-]/g, '')) || 0;
};

const productOptionsFor = form => [
  {
    label: 'Serviço personalizado',
    value: CUSTOM_SERVICE_VALUE,
    productType: CUSTOM_SERVICE_PRODUCT_TYPE,
    productId: null,
    name: form.custom_name || 'Serviço personalizado',
    price: 0,
  },
  ...creditProducts.value.map(product => ({
    label: `Crédito · ${product.name}`,
    value: `TalkviaCreditProduct:${product.id}`,
    productType: 'TalkviaCreditProduct',
    productId: product.id,
    name: product.name,
    price: productPrice(product.sale_price, product.value),
  })),
  ...inventoryProducts.value.map(product => ({
    label: `Estoque · ${product.name}`,
    value: `TalkviaInventoryProduct:${product.id}`,
    productType: 'TalkviaInventoryProduct',
    productId: product.id,
    name: product.name,
    price: productPrice(product.sale_price),
  })),
  ...inventoryProductGroups.value.map(group => ({
    label: `Grupo · ${group.name}`,
    value: `TalkviaInventoryProductGroup:${group.id}`,
    productType: 'TalkviaInventoryProductGroup',
    productId: group.id,
    name: group.name,
    price: productPrice(group.sale_price),
  })),
];

const productOptions = computed(() => productOptionsFor(invoiceItemForm));
const contractEditProductOptions = computed(() =>
  productOptionsFor(contractEditInvoiceItemForm)
);

const templateKind = template => template.metadata?.kind || 'contract';

const contractTemplates = computed(() =>
  templates.value.filter(template => templateKind(template) === 'contract')
);

const quoteTemplates = computed(() =>
  templates.value.filter(template => templateKind(template) === 'quote')
);

const visibleTemplates = computed(() =>
  templates.value.filter(
    template => templateKind(template) === templateModalKind.value
  )
);

const selectedTemplate = computed(() =>
  contractTemplates.value.find(
    template => template.id === Number(selectedTemplateId.value)
  )
);

const activeTemplateOptions = computed(() =>
  contractTemplates.value
    .filter(item => item.active)
    .map(template => ({ label: template.name, value: template.id }))
);

const generateTemplateLabel = computed(
  () =>
    activeTemplateOptions.value.find(
      option =>
        Number(option.value) ===
        Number(contractForm.talkvia_contract_template_id)
    )?.label || 'Selecione o modelo'
);

const generateTemplateMenuItems = computed(() =>
  activeTemplateOptions.value.map(option => ({
    ...option,
    action: 'generate_template',
    icon:
      Number(option.value) === Number(contractForm.talkvia_contract_template_id)
        ? 'i-lucide-check'
        : '',
    isSelected:
      Number(option.value) ===
      Number(contractForm.talkvia_contract_template_id),
  }))
);

const paymentMethodMenuItems = selectedValue =>
  allPaymentMethods.value.map(method => ({
    label: method,
    value: method,
    action: 'payment_method',
    icon: method === selectedValue ? 'i-lucide-check' : '',
    isSelected: method === selectedValue,
  }));

const generatePaymentMethodLabel = computed(
  () => contractForm.payment_method || 'Selecione'
);

const generatePaymentMethodMenuItems = computed(() =>
  paymentMethodMenuItems(contractForm.payment_method)
);

const editPaymentMethodLabel = computed(
  () => contractEditForm.payment_method || 'Não informado'
);

const editPaymentMethodMenuItems = computed(() => [
  {
    label: 'Não informado',
    value: '',
    action: 'edit_payment_method',
    icon: !contractEditForm.payment_method ? 'i-lucide-check' : '',
    isSelected: !contractEditForm.payment_method,
  },
  ...paymentMethodMenuItems(contractEditForm.payment_method).map(item => ({
    ...item,
    action: 'edit_payment_method',
  })),
]);

const productSelectLabel = computed(
  () =>
    productOptions.value.find(
      option => option.value === invoiceItemForm.product_value
    )?.label || 'Selecione o produto ou serviço'
);

const contractEditProductSelectLabel = computed(
  () =>
    contractEditProductOptions.value.find(
      option => option.value === contractEditInvoiceItemForm.product_value
    )?.label || 'Selecione o produto ou serviço'
);

const isCustomServiceItem = computed(
  () => invoiceItemForm.product_value === CUSTOM_SERVICE_VALUE
);

const isContractEditCustomServiceItem = computed(
  () => contractEditInvoiceItemForm.product_value === CUSTOM_SERVICE_VALUE
);

const canAddInvoiceItem = computed(() => {
  if (!invoiceItemForm.product_value) return false;
  if (isCustomServiceItem.value && !invoiceItemForm.custom_name.trim()) {
    return false;
  }
  return true;
});

const canAddContractEditInvoiceItem = computed(() => {
  if (!contractEditInvoiceItemForm.product_value) return false;
  if (
    isContractEditCustomServiceItem.value &&
    !contractEditInvoiceItemForm.custom_name.trim()
  ) {
    return false;
  }
  return true;
});

const productMenuItems = computed(() =>
  productOptions.value.map(product => ({
    label: product.label,
    value: product.value,
    action: 'invoice_product',
    icon:
      product.value === invoiceItemForm.product_value ? 'i-lucide-check' : '',
    isSelected: product.value === invoiceItemForm.product_value,
  }))
);

const contractEditProductMenuItems = computed(() =>
  contractEditProductOptions.value.map(product => ({
    label: product.label,
    value: product.value,
    action: 'edit_invoice_product',
    icon:
      product.value === contractEditInvoiceItemForm.product_value
        ? 'i-lucide-check'
        : '',
    isSelected: product.value === contractEditInvoiceItemForm.product_value,
  }))
);

const toggleFormSelectMenu = menu => {
  openFormSelectMenu.value = openFormSelectMenu.value === menu ? '' : menu;
};

const closeFormSelectMenu = () => {
  openFormSelectMenu.value = '';
};

const setFormSelectMenuRef = (key, element) => {
  if (element) {
    formSelectMenuRefs.value[key] = element;
  } else {
    delete formSelectMenuRefs.value[key];
  }
};

const chooseGenerateTemplate = ({ value }) => {
  contractForm.talkvia_contract_template_id = value;
  closeFormSelectMenu();
};

const chooseGeneratePaymentMethod = ({ value }) => {
  contractForm.payment_method = value;
  closeFormSelectMenu();
};

const chooseEditPaymentMethod = ({ value }) => {
  contractEditForm.payment_method = value;
  closeFormSelectMenu();
};

const quoteTemplateBody = () =>
  [
    '<p><strong>PROPOSTA DE LOCAÇÃO Nº {{numero_orcamento}}</strong></p>',
    '<p>{{data_geracao}}</p>',
    '<p><strong>Identificação:</strong> {{nome}}</p>',
    '<p><strong>Para:</strong> {{nome}}<br><strong>{{tipo_documento}}:</strong> {{documento}}<br><strong>Telefone:</strong> {{telefone}}<br><strong>Endereço:</strong> {{endereco_completo}}</p>',
    '<p><strong>Período(s)</strong><br><strong>Saída:</strong> {{data_entrega}} &nbsp; <strong>Retorno:</strong> {{data_retirada}}</p>',
    '<p><strong>Produto(s)</strong></p>',
    '{{produtos_tabela}}',
    '<p><strong>Total Geral:</strong> {{total}}</p>',
    '<p><strong>Pagamento(s):</strong> {{forma_pagamento}}</p>',
    '<p><strong>Observações:</strong></p>',
  ].join('');

const resetTemplateForm = (kind = templateModalKind.value) => {
  templateModalKind.value = kind;
  Object.assign(templateForm, {
    id: null,
    name: '',
    body: kind === 'quote' ? quoteTemplateBody() : '',
    active: true,
    require_selfie: kind !== 'quote',
    metadata: {
      kind,
      required_documents: kind === 'quote' ? [] : [...DEFAULT_DOCUMENTS],
    },
  });
};

const requiredDocuments = computed({
  get: () => templateForm.metadata?.required_documents || [],
  set: value => {
    templateForm.metadata = {
      ...(templateForm.metadata || {}),
      required_documents: value,
    };
  },
});

const insertParameter = parameter => {
  const editor = document.querySelector('[data-contract-editor] .ProseMirror');
  if (!editor) return;

  editor.focus();
  document.execCommand('insertText', false, parameter);
  editor.dispatchEvent(new InputEvent('input', { bubbles: true }));
};

const toggleRequiredDocument = documentName => {
  if (requiredDocuments.value.includes(documentName)) {
    requiredDocuments.value = requiredDocuments.value.filter(
      item => item !== documentName
    );
    return;
  }

  requiredDocuments.value = [...requiredDocuments.value, documentName];
};

const addCustomDocument = () => {
  const value = customDocumentName.value.trim();
  if (!value || requiredDocuments.value.includes(value)) return;

  requiredDocuments.value = [...requiredDocuments.value, value];
  customDocumentName.value = '';
};

const selectedInvoiceProduct = computed(() =>
  productOptions.value.find(
    item => item.value === invoiceItemForm.product_value
  )
);

const selectedContractEditInvoiceProduct = computed(() =>
  contractEditProductOptions.value.find(
    item => item.value === contractEditInvoiceItemForm.product_value
  )
);

const invoiceTotal = computed(() =>
  invoiceItems.value.reduce((total, item) => total + Number(item.total || 0), 0)
);

const contractEditInvoiceTotal = computed(() =>
  contractEditInvoiceItems.value.reduce(
    (total, item) => total + Number(item.total || 0),
    0
  )
);

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

const toLocalDatetimeInput = value => {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const parseLocalDateTime = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const contractDateTimeRange = computed(() => [
  parseLocalDateTime(contractForm.scheduled_at),
  parseLocalDateTime(contractForm.pickup_at),
]);

const contractEditDateTimeRange = computed(() => [
  parseLocalDateTime(contractEditForm.scheduled_at),
  parseLocalDateTime(contractEditForm.pickup_at),
]);

const updateContractDateTimeRange = (form, rangeTypeRef, value) => {
  const [start, end, selectedRange] = Array.isArray(value) ? value : [];
  form.scheduled_at = toLocalDatetimeInput(start);
  form.pickup_at = toLocalDatetimeInput(end);
  rangeTypeRef.value = selectedRange || DATE_RANGE_TYPES.CUSTOM_RANGE;
};

const formatDateTime = value =>
  value
    ? new Date(value).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '-';

const toDateInputValue = date => {
  const value = new Date(date);
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
};

const parseDateInputValue = value => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const addDays = (date, days) => {
  const value = new Date(date);
  value.setDate(value.getDate() + days);
  return value;
};

const addMonths = (date, months) => {
  const value = new Date(date);
  value.setMonth(value.getMonth() + months);
  return value;
};

const startOfWeek = date => {
  const value = new Date(date);
  const day = value.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  value.setDate(value.getDate() + diff);
  return value;
};

const endOfMonth = date => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const formatDateLabel = value =>
  value ? parseDateInputValue(value).toLocaleDateString('pt-BR') : 'Selecionar';

const todayDateTimeInput = () => `${toDateInputValue(new Date())}T00:00`;

const openNativeDatePicker = event => {
  const input = event.currentTarget.parentElement?.querySelector('input');
  if (!input) return;

  input.focus();
  if (typeof input.showPicker === 'function') {
    input.showPicker();
    return;
  }

  input.click();
};

const formatDateRangeLabel = (start, end) => {
  if (!start && !end) return 'Selecionar período';
  if (start && !end) return formatDateLabel(start);
  return `${formatDateLabel(start)} - ${formatDateLabel(end)}`;
};

const presetRange = presetKey => {
  const today = new Date();
  const start = new Date(today);
  const end = new Date(today);

  if (presetKey === 'last7') start.setDate(today.getDate() - 6);
  if (presetKey === 'last30') start.setDate(today.getDate() - 29);
  if (presetKey === 'last3m') start.setMonth(today.getMonth() - 3);
  if (presetKey === 'last6m') start.setMonth(today.getMonth() - 6);
  if (presetKey === 'lastYear') {
    return {
      start: toDateInputValue(new Date(today.getFullYear() - 1, 0, 1)),
      end: toDateInputValue(new Date(today.getFullYear() - 1, 11, 31)),
    };
  }
  if (presetKey === 'thisWeek') {
    const weekStart = startOfWeek(today);
    return {
      start: toDateInputValue(weekStart),
      end: toDateInputValue(addDays(weekStart, 6)),
    };
  }
  if (presetKey === 'thisMonth') {
    return {
      start: toDateInputValue(
        new Date(today.getFullYear(), today.getMonth(), 1)
      ),
      end: toDateInputValue(endOfMonth(today)),
    };
  }

  return {
    start: toDateInputValue(start),
    end: toDateInputValue(end),
  };
};

const setDateRangePreset = presetKey => {
  dateRangeDraft.preset = presetKey;
  if (presetKey === 'custom') return;

  const range = presetRange(presetKey);
  dateRangeDraft.start = range.start;
  dateRangeDraft.end = range.end;
  dateRangeDraft.month = range.start;
};

const openDateRangeFilter = filter => {
  activeDateRangeFilter.value =
    activeDateRangeFilter.value === filter.key ? '' : filter.key;
  dateRangeDraft.start = filters[filter.startKey] || '';
  dateRangeDraft.end = filters[filter.endKey] || '';
  dateRangeDraft.preset = 'custom';
  dateRangeDraft.month = dateRangeDraft.start || toDateInputValue(new Date());
};

const dateRangeButtonLabel = filter =>
  formatDateRangeLabel(filters[filter.startKey], filters[filter.endKey]);

const monthTitle = month =>
  month.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });

const buildCalendarMonthFrom = (monthValue, offset = 0) => {
  const baseDate = parseDateInputValue(monthValue) || new Date();
  const month = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth() + offset,
    1
  );
  const gridStart = startOfWeek(month);
  const days = Array.from({ length: 42 }, (_, index) => {
    const date = addDays(gridStart, index);
    return {
      date,
      value: toDateInputValue(date),
      label: date.getDate(),
      isCurrentMonth: date.getMonth() === month.getMonth(),
    };
  });

  return { month, days };
};

const buildCalendarMonth = offset =>
  buildCalendarMonthFrom(dateRangeDraft.month, offset);

const calendarMonths = computed(() => [
  buildCalendarMonth(0),
  buildCalendarMonth(1),
]);

const stockCalendarMonth = computed(() =>
  buildCalendarMonthFrom(stockDateMonth.value)
);

const shiftCalendarMonth = direction => {
  const baseDate = parseDateInputValue(dateRangeDraft.month) || new Date();
  dateRangeDraft.month = toDateInputValue(addMonths(baseDate, direction));
};

const selectDateRangeDay = value => {
  dateRangeDraft.preset = 'custom';
  if (!dateRangeDraft.start || (dateRangeDraft.start && dateRangeDraft.end)) {
    dateRangeDraft.start = value;
    dateRangeDraft.end = '';
    return;
  }

  if (value < dateRangeDraft.start) {
    dateRangeDraft.end = dateRangeDraft.start;
    dateRangeDraft.start = value;
    return;
  }

  dateRangeDraft.end = value;
};

const calendarDayClass = day => {
  const isStart = day.value === dateRangeDraft.start;
  const isEnd = day.value === dateRangeDraft.end;
  const isInRange =
    dateRangeDraft.start &&
    dateRangeDraft.end &&
    day.value > dateRangeDraft.start &&
    day.value < dateRangeDraft.end;

  return [
    day.isCurrentMonth ? 'text-n-slate-12' : 'text-n-slate-8',
    isInRange ? 'bg-n-brand/20 text-n-slate-12' : '',
    isStart || isEnd
      ? 'bg-n-brand text-white hover:bg-n-brand'
      : 'hover:bg-n-alpha-2',
    isStart && !dateRangeDraft.end ? 'rounded-lg' : '',
    isStart && dateRangeDraft.end ? 'rounded-l-lg' : '',
    isEnd ? 'rounded-r-lg' : '',
  ];
};

const stockCalendarDayClass = day => [
  day.isCurrentMonth ? 'text-n-slate-12' : 'text-n-slate-8',
  day.value === stockDate.value
    ? 'rounded-lg bg-n-brand text-white hover:bg-n-brand'
    : 'rounded-lg hover:bg-n-alpha-2',
];

const openStockDatePicker = () => {
  stockDateMonth.value = stockDate.value || toDateInputValue(new Date());
  showStockDatePicker.value = !showStockDatePicker.value;
};

const shiftStockDateMonth = direction => {
  const baseDate = parseDateInputValue(stockDateMonth.value) || new Date();
  stockDateMonth.value = toDateInputValue(addMonths(baseDate, direction));
};

const selectStockDate = value => {
  stockDate.value = value;
  stockDateMonth.value = value;
  showStockDatePicker.value = false;
};

const parseCurrency = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return Number(digits || 0) / 100;
};

const maskCurrency = event => {
  invoiceItemForm.unit_price = formatCurrency(
    parseCurrency(event.target.value)
  );
};

const maskContractEditCurrency = event => {
  contractEditInvoiceItemForm.unit_price = formatCurrency(
    parseCurrency(event.target.value)
  );
};

const updateInvoiceItemQuantity = (items, index, value) => {
  const quantity = Math.max(Number(value || 1), 1);
  const item = items[index];
  if (!item) return;

  items[index] = {
    ...item,
    quantity,
    total: quantity * Number(item.unit_price || 0),
  };
};

const updateInvoiceItemUnitPrice = (items, index, event) => {
  const item = items[index];
  if (!item) return;

  const unitPrice = parseCurrency(event.target.value);
  event.target.value = formatCurrency(unitPrice);
  items[index] = {
    ...item,
    unit_price: unitPrice,
    total: Number(item.quantity || 1) * unitPrice,
  };
};

const syncSelectedProductPrice = () => {
  if (isCustomServiceItem.value) {
    invoiceItemForm.unit_price =
      invoiceItemForm.unit_price || formatCurrency(0);
    return;
  }

  invoiceItemForm.unit_price = selectedInvoiceProduct.value
    ? formatCurrency(selectedInvoiceProduct.value.price)
    : '';
};

const syncSelectedContractEditProductPrice = () => {
  if (isContractEditCustomServiceItem.value) {
    contractEditInvoiceItemForm.unit_price =
      contractEditInvoiceItemForm.unit_price || formatCurrency(0);
    return;
  }

  contractEditInvoiceItemForm.unit_price =
    selectedContractEditInvoiceProduct.value
      ? formatCurrency(selectedContractEditInvoiceProduct.value.price)
      : '';
};

const chooseInvoiceProduct = ({ value }) => {
  invoiceItemForm.product_value = value;
  syncSelectedProductPrice();
  closeFormSelectMenu();
};

const chooseContractEditInvoiceProduct = ({ value }) => {
  contractEditInvoiceItemForm.product_value = value;
  syncSelectedContractEditProductPrice();
  closeFormSelectMenu();
};

const addInvoiceItem = () => {
  if (!selectedInvoiceProduct.value || !canAddInvoiceItem.value) return;

  const quantity = Math.max(Number(invoiceItemForm.quantity || 1), 1);
  const unitPrice = isCustomServiceItem.value
    ? parseCurrency(invoiceItemForm.unit_price)
    : parseCurrency(invoiceItemForm.unit_price) ||
      Number(selectedInvoiceProduct.value.price || 0);
  const productName = isCustomServiceItem.value
    ? invoiceItemForm.custom_name.trim()
    : selectedInvoiceProduct.value.name;

  invoiceItems.value = [
    ...invoiceItems.value,
    {
      product_type: selectedInvoiceProduct.value.productType,
      product_id: selectedInvoiceProduct.value.productId,
      product_name: productName,
      quantity,
      unit_price: unitPrice,
      total: quantity * unitPrice,
    },
  ];

  Object.assign(invoiceItemForm, {
    product_value: '',
    custom_name: '',
    quantity: 1,
    unit_price: '',
  });
};

const findProductOptionForInvoiceItem = item => {
  const productType = item?.product_type || item?.productType;
  const productId = item?.product_id || item?.productId;
  const productName = item?.product_name || item?.productName;

  return contractEditProductOptions.value.find(option => {
    if (productType && option.productType !== productType) return false;
    if (productId) return Number(option.productId) === Number(productId);
    return productName && option.name === productName;
  });
};

const isBlankInvoiceItemValue = (key, value, fallback = {}) => {
  if (value === null || value === undefined || value === '') return true;
  if ((key === 'product_name' || key === 'productName') && value === 'Item') {
    return !!fallback.product_name;
  }
  if ((key === 'unit_price' || key === 'unitPrice') && Number(value) === 0) {
    return (
      Number(fallback.unit_price || fallback.unitPrice || fallback.total || 0) >
      0
    );
  }
  if (key === 'total' && Number(value) === 0) {
    return Number(fallback.total || 0) > 0;
  }
  return false;
};

const normalizeInvoiceItem = (item, fallback = {}) => {
  const mergedItem = {
    ...fallback,
    ...Object.fromEntries(
      Object.entries(item || {}).filter(
        ([key, value]) => !isBlankInvoiceItemValue(key, value, fallback)
      )
    ),
  };
  const productOption = findProductOptionForInvoiceItem(mergedItem);
  const rawQuantity = Number(mergedItem.quantity || 0);
  const quantity = rawQuantity > 0 ? rawQuantity : 1;
  const normalizedTotal = Number(mergedItem.total || fallback.total || 0);
  const unitPrice =
    Number(mergedItem.unit_price ?? mergedItem.unitPrice ?? 0) ||
    Number(productOption?.price || 0) ||
    normalizedTotal / quantity;

  const savedProductName = mergedItem.product_name || mergedItem.productName;
  const hasGenericProductName =
    !savedProductName || savedProductName === 'Item';

  return {
    product_type:
      mergedItem.product_type ||
      mergedItem.productType ||
      productOption?.productType ||
      '',
    product_id:
      mergedItem.product_id ||
      mergedItem.productId ||
      productOption?.productId ||
      null,
    product_name: hasGenericProductName
      ? productOption?.name || fallback.product_name || 'Item'
      : savedProductName,
    quantity,
    unit_price: unitPrice,
    total: Number(normalizedTotal || quantity * unitPrice),
  };
};

const resetContractEditInvoiceItemForm = () => {
  Object.assign(contractEditInvoiceItemForm, {
    product_value: '',
    custom_name: '',
    quantity: 1,
    unit_price: '',
  });
};

const addContractEditInvoiceItem = () => {
  if (
    !selectedContractEditInvoiceProduct.value ||
    !canAddContractEditInvoiceItem.value
  ) {
    return;
  }

  const quantity = Math.max(
    Number(contractEditInvoiceItemForm.quantity || 1),
    1
  );
  const unitPrice = isContractEditCustomServiceItem.value
    ? parseCurrency(contractEditInvoiceItemForm.unit_price)
    : parseCurrency(contractEditInvoiceItemForm.unit_price) ||
      Number(selectedContractEditInvoiceProduct.value.price || 0);
  const productName = isContractEditCustomServiceItem.value
    ? contractEditInvoiceItemForm.custom_name.trim()
    : selectedContractEditInvoiceProduct.value.name;

  contractEditInvoiceItems.value = [
    ...contractEditInvoiceItems.value,
    {
      product_type: selectedContractEditInvoiceProduct.value.productType,
      product_id: selectedContractEditInvoiceProduct.value.productId,
      product_name: productName,
      quantity,
      unit_price: unitPrice,
      total: quantity * unitPrice,
    },
  ];

  resetContractEditInvoiceItemForm();
};

const removeInvoiceItem = index => {
  invoiceItems.value = invoiceItems.value.filter(
    (_, itemIndex) => itemIndex !== index
  );
};

const removeContractEditInvoiceItem = index => {
  contractEditInvoiceItems.value = contractEditInvoiceItems.value.filter(
    (_, itemIndex) => itemIndex !== index
  );
};

const fetchTemplates = async () => {
  const { data } = await ContractTemplatesAPI.get();
  templates.value = data;
  const firstContractTemplate = data.find(
    template => templateKind(template) === 'contract'
  );
  if (!selectedTemplateId.value && firstContractTemplate) {
    selectedTemplateId.value = firstContractTemplate.id;
    contractForm.talkvia_contract_template_id = firstContractTemplate.id;
  }
};

const endOfDayISO = value => {
  if (!value) return '';
  const date = parseDateInputValue(value);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
};

const startOfDayISO = value => {
  if (!value) return '';
  const date = parseDateInputValue(value);
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};

const contractFilterParams = () => ({
  ...filters,
  start_at: filters.start_at ? startOfDayISO(filters.start_at) : undefined,
  end_at: filters.end_at ? endOfDayISO(filters.end_at) : undefined,
  pickup_start_at: filters.pickup_start_at
    ? startOfDayISO(filters.pickup_start_at)
    : undefined,
  pickup_end_at: filters.pickup_end_at
    ? endOfDayISO(filters.pickup_end_at)
    : undefined,
});

const fetchContracts = async () => {
  const { data } = await ContractsAPI.get(contractFilterParams());
  contracts.value = data.contracts || [];
  summary.value = data.summary || {};
};

const contractStatusFilterLabel = computed(
  () => statusOptions.find(option => option.value === filters.status)?.label
);

const contractStatusFilterItems = computed(() =>
  statusOptions.map(option => ({
    label: option.label,
    action: 'status_filter',
    value: option.value,
    icon: option.value === filters.status ? 'i-lucide-check' : '',
    isSelected: option.value === filters.status,
  }))
);

const applyContractStatusFilter = async ({ value }) => {
  filters.status = value;
  showContractStatusFilter.value = false;
  await fetchContracts();
};

const setDateRangeFilterRef = (key, element) => {
  if (element) {
    dateRangeFilterRefs.value[key] = element;
  } else {
    delete dateRangeFilterRefs.value[key];
  }
};

const handleDateRangeOutsidePointerDown = event => {
  if (showStockDatePicker.value) {
    const clickedInsideStockPicker = stockDatePickerRef.value?.contains(
      event.target
    );
    if (!clickedInsideStockPicker) showStockDatePicker.value = false;
  }

  if (openFormSelectMenu.value) {
    const activeSelectElement =
      formSelectMenuRefs.value[openFormSelectMenu.value];
    if (!activeSelectElement?.contains(event.target)) {
      closeFormSelectMenu();
    }
  }

  if (!activeDateRangeFilter.value) return;

  const activeFilterElement =
    dateRangeFilterRefs.value[activeDateRangeFilter.value];

  if (activeFilterElement?.contains(event.target)) return;

  activeDateRangeFilter.value = '';
};

const clearDateRangeFilter = async filter => {
  filters[filter.startKey] = '';
  filters[filter.endKey] = '';
  dateRangeDraft.start = '';
  dateRangeDraft.end = '';
  dateRangeDraft.preset = 'custom';
  activeDateRangeFilter.value = '';
  await fetchContracts();
};

const applyDateRangeFilter = async filter => {
  filters[filter.startKey] = dateRangeDraft.start;
  filters[filter.endKey] = dateRangeDraft.end || dateRangeDraft.start;
  activeDateRangeFilter.value = '';
  await fetchContracts();
};

const isDashboardLoading = ref(false);
const dashboardDateRange = reactive({ start: '', end: '' });

const fetchAllContractsForDashboard = async () => {
  isDashboardLoading.value = true;
  try {
    const params = { limit: 2000 };
    if (dashboardDateRange.start)
      params.created_start_at = new Date(
        dashboardDateRange.start
      ).toISOString();
    if (dashboardDateRange.end) {
      const end = new Date(dashboardDateRange.end);
      end.setHours(23, 59, 59, 999);
      params.created_end_at = end.toISOString();
    }
    const { data } = await ContractsAPI.get(params);
    allContracts.value = data.contracts || [];
  } finally {
    isDashboardLoading.value = false;
  }
};

const applyDashboardDateRange = () => fetchAllContractsForDashboard();

const setDashboardPreset = preset => {
  const now = new Date();
  if (preset === 'month') {
    dashboardDateRange.start = new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
    dashboardDateRange.end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10);
  } else if (preset === '30d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    dashboardDateRange.start = d.toISOString().slice(0, 10);
    dashboardDateRange.end = now.toISOString().slice(0, 10);
  } else if (preset === 'year') {
    dashboardDateRange.start = new Date(now.getFullYear(), 0, 1)
      .toISOString()
      .slice(0, 10);
    dashboardDateRange.end = new Date(now.getFullYear(), 11, 31)
      .toISOString()
      .slice(0, 10);
  } else {
    dashboardDateRange.start = '';
    dashboardDateRange.end = '';
  }
  fetchAllContractsForDashboard();
};

const fetchProducts = async () => {
  const [creditResponse, inventoryResponse, groupsResponse] = await Promise.all(
    [
      TalkviaCreditProductsAPI.get(),
      TalkviaInventoryProductsAPI.get({ active: true }),
      TalkviaInventoryProductGroupsAPI.get({ active: true }),
    ]
  );
  creditProducts.value = creditResponse.data || [];
  inventoryProducts.value = inventoryResponse.data.products || [];
  inventoryProductGroups.value = groupsResponse.data || [];
};

const fetchStockByDate = async () => {
  const products = inventoryProducts.value.filter(p => p.active !== false);
  if (!products.length) return;
  isLoadingStock.value = true;
  try {
    const results = await Promise.all(
      products.map(p =>
        TalkviaInventoryProductsAPI.stockByDate(p.id, stockDate.value).then(
          r => r.data
        )
      )
    );
    stockByDateResults.value = results;
  } catch {
    stockByDateResults.value = [];
  } finally {
    isLoadingStock.value = false;
  }
};

const loadCustomPaymentMethods = () => {
  customPaymentMethods.value =
    currentAccount.value?.settings?.custom_payment_methods || [];
};

const saveNewPaymentMethod = async () => {
  const value = newPaymentMethodInput.value.trim();
  if (
    !value ||
    allPaymentMethods.value
      .map(m => m.toLowerCase())
      .includes(value.toLowerCase())
  )
    return;

  isSavingPaymentMethod.value = true;
  try {
    const updated = [...customPaymentMethods.value, value];
    await updateAccount({ custom_payment_methods: updated });
    customPaymentMethods.value = updated;
    newPaymentMethodInput.value = '';
    showAddPaymentInput.value = false;
    useAlert(copy.paymentMethodSaved);
  } finally {
    isSavingPaymentMethod.value = false;
  }
};

const removeCustomPaymentMethod = async method => {
  const updated = customPaymentMethods.value.filter(m => m !== method);
  await updateAccount({ custom_payment_methods: updated });
  customPaymentMethods.value = updated;
  if (contractForm.payment_method === method) contractForm.payment_method = '';
};

const searchContacts = async () => {
  if (contactSearch.value.trim().length < 2) {
    contactResults.value = [];
    return;
  }

  isSearchingContacts.value = true;
  try {
    const { data } = await ContactsAPI.search(contactSearch.value, 1);
    contactResults.value = data.payload || [];
  } finally {
    isSearchingContacts.value = false;
  }
};

const contactAttribute = (contact, keys) => {
  const source = {
    ...(contact?.additional_attributes || {}),
    ...(contact?.custom_attributes || {}),
  };
  return keys.map(key => source[key]).find(value => value) || '';
};

const contactDocuments = contact =>
  [
    {
      type: 'cpf',
      label: 'CPF',
      value: contactAttribute(contact, ['cpf', 'CPF', 'document', 'documento']),
    },
    {
      type: 'cnpj',
      label: 'CNPJ',
      value: contactAttribute(contact, ['cnpj', 'CNPJ']),
    },
  ].filter(document => document.value);

const contactCpf = contact => contactDocuments(contact)[0]?.value || '';

const selectedContactDocuments = computed(() =>
  contactDocuments(selectedContact.value)
);

const recipientNameForDocument = (contact, documentType) => {
  if (documentType !== 'cnpj') return contact?.name || '';

  return (
    contactAttribute(contact, [
      'razao_social',
      'razaoSocial',
      'nome_fantasia',
      'nomeFantasia',
    ]) ||
    contact?.name ||
    ''
  );
};

const selectRecipientDocument = documentType => {
  const document = selectedContactDocuments.value.find(
    item => item.type === documentType
  );
  if (!document) return;

  selectedDocumentType.value = document.type;
  contractForm.recipient_cpf = document.value;
  contractForm.recipient_name = recipientNameForDocument(
    selectedContact.value,
    document.type
  );
};

const extractContactAddress = contact => ({
  recipient_zip_code: contactAttribute(contact, [
    'cep',
    'zip_code',
    'zipCode',
    'zipcode',
    'postal_code',
  ]),
  recipient_address: contactAttribute(contact, [
    'endereco',
    'address',
    'addressLine',
    'address_line',
    'street',
    'logradouro',
  ]),
  recipient_address_number: contactAttribute(contact, [
    'numero',
    'number',
    'addressNumber',
    'address_number',
  ]),
  recipient_address_complement: contactAttribute(contact, [
    'complemento',
    'complement',
    'addressComplement',
    'address_complement',
  ]),
  recipient_neighborhood: contactAttribute(contact, [
    'bairro',
    'neighborhood',
    'district',
  ]),
  recipient_city: contactAttribute(contact, ['cidade', 'city']),
  recipient_state: contactAttribute(contact, ['uf', 'state', 'province']),
});

const applyAddressToContract = address => {
  Object.assign(contractForm, address);
};

const extractContractAddress = contract => {
  const address = contract.metadata?.recipient_address || {};
  return {
    recipient_zip_code: address.zip_code || '',
    recipient_address: address.address || '',
    recipient_address_number: address.number || '',
    recipient_address_complement: address.complement || '',
    recipient_neighborhood: address.neighborhood || '',
    recipient_city: address.city || '',
    recipient_state: address.state || '',
    recipient_address_source:
      contract.metadata?.recipient_address_source || 'contract',
  };
};

const cleanZipCode = value => String(value || '').replace(/\D/g, '');

const applyZipCodeAddress = (form, zipCode, data) => {
  Object.assign(form, {
    recipient_zip_code: `${zipCode.slice(0, 5)}-${zipCode.slice(5)}`,
    recipient_address: data.logradouro || form.recipient_address,
    recipient_neighborhood: data.bairro || form.recipient_neighborhood,
    recipient_city: data.localidade || form.recipient_city,
    recipient_state: data.uf || form.recipient_state,
    recipient_address_complement:
      data.complemento || form.recipient_address_complement,
  });
};

const lookupZipCodeForForm = async ({
  form,
  force = false,
  isFetchingRef,
  lastLookupRef,
}) => {
  const zipCode = cleanZipCode(form.recipient_zip_code);
  if (zipCode.length !== 8) return;
  if (!force && zipCode === lastLookupRef.value) return;

  isFetchingRef.value = true;
  lastLookupRef.value = zipCode;
  try {
    const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`);
    const data = await response.json();
    if (!response.ok || data.erro) throw new Error(copy.zipCodeNotFound);

    applyZipCodeAddress(form, zipCode, data);
  } catch {
    useAlert(copy.zipCodeNotFound);
  } finally {
    isFetchingRef.value = false;
  }
};

const lookupContractZipCode = async (force = false) => {
  if (useContactAddress.value) return;

  await lookupZipCodeForForm({
    form: contractForm,
    force,
    isFetchingRef: isFetchingContractZipCode,
    lastLookupRef: lastContractZipCodeLookup,
  });
};

const lookupContractEditZipCode = async (force = false) => {
  await lookupZipCodeForForm({
    form: contractEditForm,
    force,
    isFetchingRef: isFetchingContractEditZipCode,
    lastLookupRef: lastContractEditZipCodeLookup,
  });
};

const setUseContactAddress = shouldUseContactAddress => {
  useContactAddress.value = shouldUseContactAddress;
  contractForm.recipient_address_source = shouldUseContactAddress
    ? 'contact'
    : 'contract';
  if (shouldUseContactAddress) {
    applyAddressToContract(selectedContactAddress.value);
  }
};

const selectContact = contact => {
  selectedContact.value = contact;
  contractForm.contact_id = contact.id;
  contractForm.recipient_phone = contact.phone_number || '';
  const documents = contactDocuments(contact);
  const preferredType =
    contactAttribute(contact, ['tipo_pessoa', 'tipoPessoa']) === 'pj'
      ? 'cnpj'
      : 'cpf';
  const selectedDocument =
    documents.find(document => document.type === preferredType) || documents[0];
  selectedDocumentType.value = selectedDocument?.type || '';
  contractForm.recipient_cpf = selectedDocument?.value || '';
  contractForm.recipient_name = recipientNameForDocument(
    contact,
    selectedDocument?.type
  );
  selectedContactAddress.value = extractContactAddress(contact);
  if (useContactAddress.value) {
    applyAddressToContract(selectedContactAddress.value);
  }
  contactSearch.value =
    `${contact.name || 'Contato'} ${contact.phone_number || ''}`.trim();
  contactResults.value = [];
};

const openGenerateContractModal = () => {
  contractForm.talkvia_contract_template_id =
    contractForm.talkvia_contract_template_id || selectedTemplateId.value;
  contractForm.scheduled_at = todayDateTimeInput();
  contractForm.pickup_at = contractForm.pickup_at || contractForm.scheduled_at;
  contractRangeType.value = DATE_RANGE_TYPES.CUSTOM_RANGE;
  showGenerateContractModal.value = true;
};

const closeGenerateContractModal = () => {
  showGenerateContractModal.value = false;
  closeFormSelectMenu();
};

const refresh = async () => {
  isLoading.value = true;
  try {
    await Promise.all([fetchTemplates(), fetchContracts(), fetchProducts()]);
    loadCustomPaymentMethods();
  } finally {
    isLoading.value = false;
  }
};

const openTemplateModal = (template = null, kind = templateModalKind.value) => {
  if (template) {
    templateModalKind.value = templateKind(template);
    Object.assign(templateForm, {
      id: template.id,
      name: template.name,
      body: template.body,
      active: template.active,
      require_selfie: template.require_selfie !== false,
      metadata: {
        ...(template.metadata || {}),
        kind: templateKind(template),
        required_documents:
          template.metadata?.required_documents ||
          (templateKind(template) === 'quote' ? [] : [...DEFAULT_DOCUMENTS]),
      },
    });
  } else {
    resetTemplateForm(kind);
  }
  showTemplateModal.value = true;
};

const saveTemplate = async () => {
  templateForm.metadata = {
    ...(templateForm.metadata || {}),
    kind: templateModalKind.value,
  };
  const payload = {
    name: templateForm.name,
    body: templateForm.body,
    active: templateForm.active,
    require_selfie: templateForm.require_selfie,
    metadata: templateForm.metadata,
  };
  if (templateForm.id) {
    await ContractTemplatesAPI.update(templateForm.id, payload);
    useAlert('Modelo atualizado.');
  } else {
    await ContractTemplatesAPI.create(payload);
    useAlert('Modelo criado.');
  }
  resetTemplateForm();
  await fetchTemplates();
};

const removeTemplate = async template => {
  await ContractTemplatesAPI.delete(template.id);
  useAlert('Modelo excluído.');
  await fetchTemplates();
};

const openQuoteDialog = () => {
  contractSendDialogRef.value?.openQuote({
    contact_id: contractForm.contact_id,
    recipient_name: contractForm.recipient_name,
    recipient_cpf: contractForm.recipient_cpf,
    recipient_phone: contractForm.recipient_phone,
    scheduled_at: contractForm.scheduled_at,
    pickup_at: contractForm.pickup_at,
    payment_method: contractForm.payment_method,
    invoice_items: invoiceItems.value,
    recipient_address: {
      zip_code: contractForm.recipient_zip_code,
      address: contractForm.recipient_address,
      number: contractForm.recipient_address_number,
      complement: contractForm.recipient_address_complement,
      neighborhood: contractForm.recipient_neighborhood,
      city: contractForm.recipient_city,
      state: contractForm.recipient_state,
    },
  });
};

function contractQuoteInvoiceItems(contract) {
  const persistedInvoiceItems = Array(
    contract.invoice_items ||
      contract.invoiceItems ||
      contract.metadata?.invoice_items ||
      contract.metadata?.invoiceItems ||
      []
  );
  const firstInvoiceItem = persistedInvoiceItems[0] || {};
  const firstItemQuantity = Number(firstInvoiceItem.quantity || 0);
  const fallbackQuantity = firstItemQuantity > 0 ? firstItemQuantity : 1;
  const fallbackTotal = Number(
    contract.invoice_total ||
      contract.invoiceTotal ||
      contract.metadata?.invoice_total ||
      contract.metadata?.invoiceTotal ||
      firstInvoiceItem.total ||
      0
  );
  const fallbackUnitPrice =
    Number(firstInvoiceItem.unit_price || firstInvoiceItem.unitPrice || 0) ||
    fallbackTotal / fallbackQuantity;
  const fallbackInvoiceItem = {
    product_type: contract.product_type || firstInvoiceItem.product_type,
    product_id: contract.product_id || firstInvoiceItem.product_id,
    product_name: contract.product_name || firstInvoiceItem.product_name,
    quantity: fallbackQuantity,
    unit_price: fallbackUnitPrice,
    total: fallbackTotal,
  };
  const sourceInvoiceItems = persistedInvoiceItems.length
    ? persistedInvoiceItems
    : [fallbackInvoiceItem];

  return sourceInvoiceItems.map(item =>
    normalizeInvoiceItem(item, fallbackInvoiceItem)
  );
}

function openContractQuoteDialog(contract) {
  contractSendDialogRef.value?.openQuote({
    contact_id: contract.contact_id,
    recipient_name: contract.recipient_name,
    recipient_cpf: contract.recipient_cpf,
    recipient_phone: contract.recipient_phone,
    scheduled_at: contract.scheduled_at,
    pickup_at: contract.pickup_at,
    payment_method: contract.payment_method,
    invoice_items: contractQuoteInvoiceItems(contract),
    quote_number: contract.id,
    contract_id: contract.id,
    public_url: contract.public_url,
    recipient_address: contract.metadata?.recipient_address || {},
  });
}

const generateContract = async ({ sendToContact = false } = {}) => {
  contractForm.talkvia_contract_template_id =
    contractForm.talkvia_contract_template_id || selectedTemplateId.value;
  const { data } = await ContractsAPI.create({
    ...contractForm,
    invoice_items: invoiceItems.value,
  });
  contracts.value = [data, ...contracts.value];
  invoiceItems.value = [];
  closeGenerateContractModal();
  await nextTick();
  if (sendToContact) {
    contractSendDialogRef.value?.open(data);
  } else {
    openContractQuoteDialog(data);
  }
  useAlert('Contrato gerado.');
  await fetchContracts();
};

const sendLink = async contract => {
  contractSendDialogRef.value?.open(contract);
};

const handleSendContractLink = async ({ contract, payload }) => {
  await ContractsAPI.sendLink(contract.id, payload);
  useAlert('Link enviado na conversa.');
};

const escapeHtml = value =>
  String(value ?? '').replace(
    /[&<>"']/g,
    character =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character]
  );

const quoteInvoiceItems = payload =>
  Array(payload?.invoice_items || []).map(item => normalizeInvoiceItem(item));

const quoteItemQuantity = item => {
  const quantity = Number(item?.quantity || 0);
  return quantity > 0 ? quantity : 1;
};

const quoteItemTotal = item =>
  Number(item?.total || 0) ||
  quoteItemQuantity(item) * Number(item?.unit_price || item?.unitPrice || 0);

const quoteTotal = payload =>
  quoteInvoiceItems(payload).reduce(
    (total, item) => total + quoteItemTotal(item),
    0
  );

const quoteProductSummary = payload =>
  quoteInvoiceItems(payload)
    .map(item => `${quoteItemQuantity(item)}x ${item.product_name || 'Item'}`)
    .join(', ');

const quoteProductList = payload =>
  quoteInvoiceItems(payload)
    .map(item => {
      const quantity = quoteItemQuantity(item);
      const unitPrice =
        Number(item.unit_price ?? item.unitPrice ?? 0) ||
        quoteItemTotal(item) / quantity;
      return `${escapeHtml(item.product_name || 'Item')} - ${quantity} x ${formatCurrency(unitPrice)} = ${formatCurrency(quoteItemTotal(item))}`;
    })
    .join('<br>');

const quoteProductTable = payload => {
  const rows = quoteInvoiceItems(payload)
    .map(item => {
      const quantity = quoteItemQuantity(item);
      const unitPrice =
        Number(item.unit_price ?? item.unitPrice ?? 0) ||
        quoteItemTotal(item) / quantity;
      return `<tr><td>${escapeHtml(item.product_name || 'Item')}</td><td>${quantity}</td><td>${formatCurrency(unitPrice)}</td><td><strong>${formatCurrency(quoteItemTotal(item))}</strong></td></tr>`;
    })
    .join('');

  if (!rows) return '';

  return `<table><thead><tr><th>Produto(s)</th><th>Qtd</th><th>Valor/Período</th><th>Total</th></tr></thead><tbody>${rows}</tbody></table>`;
};

const quoteAddress = payload => payload?.recipient_address || {};

const quoteFullAddress = payload => {
  const address = quoteAddress(payload);
  return [
    [address.address, address.number].filter(Boolean).join(', '),
    address.complement,
    address.neighborhood,
    [address.city, address.state].filter(Boolean).join(' - '),
    address.zip_code,
  ]
    .filter(Boolean)
    .join(', ');
};

const quoteTemplateValues = payload => ({
  numero: payload.quote_number || payload.contract_id || '-',
  numero_orcamento: payload.quote_number || payload.contract_id || '-',
  nome: payload.recipient_name || '-',
  cpf: payload.recipient_cpf || '-',
  cnpj:
    String(payload.recipient_cpf || '').replace(/\D/g, '').length === 14
      ? payload.recipient_cpf
      : '',
  documento: payload.recipient_cpf || '-',
  tipo_documento:
    String(payload.recipient_cpf || '').replace(/\D/g, '').length === 14
      ? 'CNPJ'
      : 'CPF',
  telefone: payload.recipient_phone || '-',
  forma_pagamento: payload.payment_method || '-',
  data: new Date().toLocaleDateString('pt-BR'),
  data_geracao: new Date().toLocaleDateString('pt-BR'),
  data_entrega: formatDateTime(payload.scheduled_at),
  data_retirada: formatDateTime(payload.pickup_at),
  produto: quoteProductSummary(payload),
  produtos: quoteProductSummary(payload),
  produtos_lista: quoteProductList(payload),
  produtos_tabela: quoteProductTable(payload),
  total: formatCurrency(quoteTotal(payload)),
  total_produtos: formatCurrency(quoteTotal(payload)),
  valor_total_produtos: formatCurrency(quoteTotal(payload)),
  cep: quoteAddress(payload).zip_code || '',
  endereco: quoteAddress(payload).address || '',
  numero_endereco: quoteAddress(payload).number || '',
  complemento: quoteAddress(payload).complement || '',
  bairro: quoteAddress(payload).neighborhood || '',
  cidade: quoteAddress(payload).city || '',
  uf: quoteAddress(payload).state || '',
  endereco_completo: quoteFullAddress(payload) || '-',
});

const renderQuoteHtml = payload => {
  const selectedQuoteTemplate = quoteTemplates.value.find(
    template => Number(template.id) === Number(payload.quote_template_id)
  );
  const body = selectedQuoteTemplate?.body || quoteTemplateBody();
  const values = quoteTemplateValues(payload);

  return body.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key) => values[key] ?? '');
};

const quotePdfDocument = payload => {
  const title = [
    'Orçamento',
    payload.recipient_name,
    payload.quote_number || payload.contract_id,
  ]
    .filter(Boolean)
    .join(' - ');
  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
    @page { size: A4; margin: 14mm; }
    body { margin: 0; color: #111827; font-family: Arial, sans-serif; font-size: 12px; line-height: 1.45; }
    .page { width: 100%; }
    p { margin: 0 0 10px; }
    strong { font-weight: 700; }
    table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; }
    th, td { border: 1px solid #111827; padding: 8px; vertical-align: top; text-align: left; }
    th { font-weight: 700; background: #f3f4f6; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style></head><body><main class="page">${renderQuoteHtml(payload)}</main></body></html>`;
};

const triggerPrint = targetWindow => {
  window.setTimeout(() => {
    try {
      targetWindow.focus();
      targetWindow.print();
    } catch {
      useAlert('Não foi possível iniciar a impressão do PDF.');
    }
  }, 250);
};

const generateQuotePdfWithIframe = payload => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.right = '0';
  iframe.style.bottom = '0';

  iframe.onload = () => {
    if (!iframe.contentWindow) {
      useAlert('Não foi possível iniciar a impressão do PDF.');
      iframe.remove();
      return;
    }

    triggerPrint(iframe.contentWindow);
    window.setTimeout(() => iframe.remove(), 1500);
  };

  iframe.srcdoc = quotePdfDocument(payload);
  document.body.appendChild(iframe);
};

const hydrateQuotePdfPayload = async payload => {
  const normalizedPayload = {
    ...payload,
    invoice_items: quoteInvoiceItems(payload),
  };
  if (!payload?.contract_id) return normalizedPayload;

  try {
    const { data: contract } = await ContractsAPI.show(payload.contract_id);
    return {
      ...normalizedPayload,
      invoice_items: contractQuoteInvoiceItems(contract),
    };
  } catch {
    return normalizedPayload;
  }
};

const handleGenerateQuotePdf = async payload => {
  const printWindow = window.open('', '_blank');
  const hydratedPayload = await hydrateQuotePdfPayload(payload);
  if (!printWindow) {
    generateQuotePdfWithIframe(hydratedPayload);
    return;
  }

  printWindow.document.open();
  printWindow.document.write(quotePdfDocument(hydratedPayload));
  printWindow.document.close();
  triggerPrint(printWindow);
};

const handleSendQuote = async payload => {
  await ContractsAPI.sendQuote(payload);
  useAlert('Orçamento enviado na conversa.');
};

const requestRemoveContract = contract => {
  contractPendingDelete.value = contract;
  deleteContractDialogRef.value?.open();
};

const closeDeleteContractDialog = () => {
  contractPendingDelete.value = null;
};

const confirmRemoveContract = async () => {
  if (!contractPendingDelete.value) return;

  isDeletingContract.value = true;
  try {
    await ContractsAPI.delete(contractPendingDelete.value.id);
    useAlert('Contrato excluído.');
    deleteContractDialogRef.value?.close();
    await fetchContracts();
  } catch {
    useAlert('Não foi possível excluir o contrato.');
  } finally {
    isDeletingContract.value = false;
  }
};

const openContract = contract => {
  window.open(contract.public_url, '_blank', 'noopener,noreferrer');
};

const openDocumentsModal = contract => {
  selectedDocuments.value = contract.metadata?.document_uploads || [];
  selectedDocumentsName.value = contract.recipient_name;
  showDocumentsModal.value = true;
};

const documentDownloadHref = doc =>
  `data:${doc.content_type};base64,${doc.data}`;

const openConversation = contract => {
  if (!contract.conversation_id) return;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: contract.conversation_id },
  });
};

const updateContractStatus = async (contract, nextStatus) => {
  if (!nextStatus || nextStatus === contract.status) return;

  updatingStatusContractId.value = contract.id;
  try {
    const { data } = await ContractsAPI.updateStatus(contract.id, nextStatus);
    Object.assign(contract, data);
    useAlert(
      nextStatus === 'pending' ? copy.pendingReopened : copy.statusUpdated
    );
    await fetchContracts();
  } catch {
    useAlert('Não foi possível alterar o status do contrato.');
  } finally {
    updatingStatusContractId.value = null;
  }
};

const statusBadgeClass = status => {
  const classes = {
    pending: 'border-n-amber-5 bg-n-amber-3 text-n-amber-11',
    signed: 'border-n-teal-5 bg-n-teal-3 text-n-teal-11',
    expired: 'border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11',
    awaiting_delivery: 'border-n-blue-5 bg-n-blue-3 text-n-blue-11',
    completed: 'border-n-green-5 bg-n-green-3 text-n-green-11',
  };
  return classes[status] || 'border-n-slate-5 bg-n-slate-3 text-n-slate-11';
};

// Dashboard metrics
const dashboardMetrics = computed(() => {
  const src = allContracts.value;
  const totalValue = src.reduce(
    (sum, c) => sum + Number(c.metadata?.invoice_total || 0),
    0
  );

  const byStatus = contractStatusOptions.map(opt => ({
    label: opt.label,
    value: opt.value,
    count: src.filter(c => c.status === opt.value).length,
  }));

  const templateMap = {};
  src.forEach(c => {
    const key = c.template_name || 'Sem modelo';
    templateMap[key] = (templateMap[key] || 0) + 1;
  });
  const byTemplate = Object.entries(templateMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  const paymentMap = {};
  src.forEach(c => {
    const key = c.payment_method || 'Não informado';
    paymentMap[key] = (paymentMap[key] || 0) + 1;
  });
  const byPayment = Object.entries(paymentMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  const withDelivery = src.filter(c => c.scheduled_at).length;
  const overdue = src.filter(
    c =>
      c.scheduled_at &&
      new Date(c.scheduled_at) < new Date() &&
      !['completed', 'cancelled'].includes(c.status)
  ).length;

  return {
    total: src.length,
    totalValue,
    byStatus,
    byTemplate,
    byPayment,
    withDelivery,
    overdue,
  };
});

const openDashboard = async () => {
  showDashboard.value = true;
  isDashboardExpanded.value = false;
  allContracts.value = [];
  dashboardDateRange.start = '';
  dashboardDateRange.end = '';
  await fetchAllContractsForDashboard();
};

const canEditContract = contract =>
  contract && !['signed', 'completed'].includes(contract.status);

const openContractEditModal = contract => {
  if (!canEditContract(contract)) {
    useAlert(copy.contractEditLocked);
    return;
  }

  editingContract.value = contract;
  contractEditRangeType.value = DATE_RANGE_TYPES.CUSTOM_RANGE;
  Object.assign(contractEditForm, {
    recipient_name: contract.recipient_name || '',
    recipient_cpf: contract.recipient_cpf || '',
    recipient_phone: contract.recipient_phone || '',
    ...extractContractAddress(contract),
    scheduled_at: toLocalDatetimeInput(contract.scheduled_at),
    pickup_at: toLocalDatetimeInput(contract.pickup_at),
    payment_method: contract.payment_method || '',
  });
  const fallbackInvoiceItem = {
    product_type: contract.product_type,
    product_id: contract.product_id,
    product_name: contract.product_name,
    quantity: 1,
    total: contract.metadata?.invoice_total,
  };
  const sourceInvoiceItems = contract.metadata?.invoice_items?.length
    ? contract.metadata.invoice_items
    : [fallbackInvoiceItem];
  contractEditInvoiceItems.value = sourceInvoiceItems.map(item =>
    normalizeInvoiceItem(item, fallbackInvoiceItem)
  );
  resetContractEditInvoiceItemForm();
  showContractEditModal.value = true;
};

const contractStatusLabel = status =>
  statusOptions.find(option => option.value === status)?.label || status || '-';

const toggleContractActionsMenu = contractId => {
  openContractStatusMenuId.value = null;
  openContractActionsId.value =
    openContractActionsId.value === contractId ? null : contractId;
};

const toggleContractStatusMenu = contractId => {
  openContractActionsId.value = null;
  openContractStatusMenuId.value =
    openContractStatusMenuId.value === contractId ? null : contractId;
};

const closeContractActionsMenu = () => {
  openContractActionsId.value = null;
  openContractStatusMenuId.value = null;
};

const contractStatusMenuItems = contract =>
  contractStatusOptions.map(option => ({
    label: option.label,
    action: 'status',
    value: option.value,
    icon: option.value === contract.status ? 'i-lucide-check' : '',
    isSelected: option.value === contract.status,
    disabled: updatingStatusContractId.value === contract.id,
  }));

const contractActionMenuItems = contract =>
  [
    canEditContract(contract)
      ? {
          label: copy.editContract,
          action: 'edit',
          value: 'edit',
          icon: 'i-lucide-pencil-line',
        }
      : null,
    {
      label: copy.openLink,
      action: 'open',
      value: 'open',
      icon: 'i-lucide-external-link',
    },
    contract.status === 'pending' &&
    (contract.conversation_id || contract.contact_id)
      ? {
          label: copy.sendLink,
          action: 'send_link',
          value: 'send_link',
          icon: 'i-lucide-send',
        }
      : null,
    contract.contact_id && contract.metadata?.invoice_items?.length
      ? {
          label: copy.sendQuote,
          action: 'send_quote',
          value: 'send_quote',
          icon: 'i-lucide-file-text',
        }
      : null,
    contract.status === 'signed' && contract.receipt_url
      ? {
          label: copy.downloadReceipt,
          action: 'receipt',
          value: 'receipt',
          icon: 'i-lucide-file-down',
        }
      : null,
    contract.metadata?.document_uploads?.length
      ? {
          label: copy.viewDocuments,
          action: 'documents',
          value: 'documents',
          icon: 'i-lucide-paperclip',
        }
      : null,
    {
      label: copy.delete,
      action: 'delete',
      value: 'delete',
      icon: 'i-lucide-trash-2',
    },
  ].filter(Boolean);

const handleContractAction = async ({ action, value }, contract) => {
  closeContractActionsMenu();

  if (action === 'status') {
    await updateContractStatus(contract, value);
  } else if (action === 'edit') {
    openContractEditModal(contract);
  } else if (action === 'open') {
    openContract(contract);
  } else if (action === 'send_link') {
    sendLink(contract);
  } else if (action === 'send_quote') {
    openContractQuoteDialog(contract);
  } else if (action === 'receipt') {
    window.open(contract.receipt_url, '_blank', 'noopener,noreferrer');
  } else if (action === 'documents') {
    openDocumentsModal(contract);
  } else if (action === 'delete') {
    requestRemoveContract(contract);
  }
};

const closeContractEditModal = () => {
  showContractEditModal.value = false;
  editingContract.value = null;
  contractEditInvoiceItems.value = [];
  resetContractEditInvoiceItemForm();
  closeFormSelectMenu();
};

const saveContractEdit = async () => {
  if (!editingContract.value) return;

  isSavingContractEdit.value = true;
  try {
    const payload = {
      ...contractEditForm,
      scheduled_at: contractEditForm.scheduled_at || null,
      pickup_at: contractEditForm.pickup_at || null,
      invoice_items: contractEditInvoiceItems.value,
    };
    const { data } = await ContractsAPI.update(editingContract.value.id, {
      ...payload,
    });
    Object.assign(editingContract.value, data);
    closeContractEditModal();
    useAlert(copy.contractUpdated);
    await fetchContracts();
  } catch {
    useAlert(copy.contractUpdateFailed);
  } finally {
    isSavingContractEdit.value = false;
  }
};

const openContractEditFromRoute = async () => {
  const contractId = Number(route.query.edit_contract_id || 0);
  if (!contractId) return;

  let contract = contracts.value.find(item => Number(item.id) === contractId);
  if (!contract) {
    try {
      const { data } = await ContractsAPI.show(contractId);
      contract = data;
    } catch {
      useAlert('Não foi possível abrir o contrato para edição.');
      return;
    }
  }

  openContractEditModal(contract);
};

const handleContractsChanged = async () => {
  await fetchContracts();
  if (showDashboard.value) {
    await fetchAllContractsForDashboard();
  }
};

onMounted(async () => {
  await refresh();
  await openContractEditFromRoute();
  window.addEventListener('talkvia:contracts-changed', handleContractsChanged);
  document.addEventListener('pointerdown', handleDateRangeOutsidePointerDown);
});

watch(
  () => route.query.edit_contract_id,
  () => openContractEditFromRoute()
);

onBeforeUnmount(() => {
  window.removeEventListener(
    'talkvia:contracts-changed',
    handleContractsChanged
  );
  document.removeEventListener(
    'pointerdown',
    handleDateRangeOutsidePointerDown
  );
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main class="flex h-full min-h-0 flex-col bg-n-background text-n-slate-12">
    <header class="border-b border-n-weak px-6 py-5">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 class="text-xl font-semibold">
            {{ copy.title }}
          </h1>
          <p class="mb-0 mt-1 text-sm text-n-slate-11">
            {{ copy.description }}
          </p>
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            class="h-10 bg-n-brand px-4 text-sm font-semibold text-white"
            @click="openGenerateContractModal"
          >
            <span
              class="i-lucide-file-plus-2 mr-1.5 inline-block size-4 align-middle"
            />
            {{ copy.generateContract }}
          </button>
          <button
            type="button"
            class="h-10 border border-n-weak bg-n-solid-2 px-4 text-sm font-semibold text-n-slate-12 transition hover:bg-n-alpha-2"
            @click="openDashboard"
          >
            <span
              class="i-lucide-bar-chart-2 mr-1.5 inline-block size-4 align-middle"
            />
            {{ copy.dashboard }}
          </button>
          <button
            type="button"
            class="h-10 border border-n-weak bg-n-solid-2 px-4 text-sm font-semibold text-n-slate-12 transition hover:bg-n-alpha-2"
            @click="openTemplateModal()"
          >
            <span
              class="i-lucide-file-text mr-1.5 inline-block size-4 align-middle"
            />
            {{ copy.manageTemplates }}
          </button>
        </div>
      </div>
    </header>

    <section class="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-6">
      <!-- Generate contract form -->
      <Teleport to="body">
        <div
          v-if="showGenerateContractModal"
          class="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm"
          @click.self="closeGenerateContractModal"
        >
          <section
            class="my-8 flex max-h-[calc(100vh-4rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
            role="dialog"
            aria-modal="true"
          >
            <header
              class="flex items-center justify-between gap-4 border-b border-n-weak px-6 py-4"
            >
              <h2 class="text-lg font-semibold">
                {{ copy.generateContract }}
              </h2>
              <button
                type="button"
                class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :aria-label="copy.close"
                @click="closeGenerateContractModal"
              >
                <span class="i-lucide-x size-5" />
              </button>
            </header>
            <div class="grid min-h-0 flex-1 gap-3 overflow-y-auto p-5">
              <div
                class="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.6fr)]"
              >
                <div class="relative">
                  <label :class="FIELD_LABEL_CLASS">
                    {{ copy.contactSearch }}
                    <input
                      v-model="contactSearch"
                      :class="INPUT_CLASS"
                      placeholder="Digite nome, telefone ou CPF/CNPJ"
                      @input="searchContacts"
                    />
                  </label>
                  <div
                    v-if="contactResults.length"
                    class="absolute z-20 mt-1 max-h-64 w-full overflow-auto border border-n-weak bg-n-solid-2 shadow-lg"
                  >
                    <button
                      v-for="contact in contactResults"
                      :key="contact.id"
                      type="button"
                      class="grid w-full gap-1 border-b border-n-weak px-3 py-2 text-left text-sm last:border-b-0 hover:bg-n-alpha-2"
                      @click="selectContact(contact)"
                    >
                      <span class="font-medium text-n-slate-12">
                        {{ contact.name || 'Sem nome' }}
                      </span>
                      <span class="text-xs text-n-slate-10">
                        {{ contact.phone_number || '-' }}
                        <template v-if="contactCpf(contact)">
                          · CPF/CNPJ {{ contactCpf(contact) }}
                        </template>
                      </span>
                    </button>
                  </div>
                  <p
                    v-if="isSearchingContacts"
                    class="mb-0 mt-1 text-xs text-n-slate-10"
                  >
                    Buscando contatos...
                  </p>
                </div>
                <div :class="FIELD_LABEL_CLASS">
                  <span>Modelo</span>
                  <div
                    :ref="el => setFormSelectMenuRef('generate-template', el)"
                    class="relative"
                  >
                    <Button
                      type="button"
                      color="slate"
                      variant="faded"
                      size="md"
                      trailing-icon
                      icon="i-lucide-chevron-down"
                      :class="[
                        SELECT_BUTTON_CLASS,
                        openFormSelectMenu === 'generate-template'
                          ? SELECT_BUTTON_OPEN_CLASS
                          : '',
                      ]"
                      :label="generateTemplateLabel"
                      @click="toggleFormSelectMenu('generate-template')"
                    />
                    <DropdownMenu
                      v-if="openFormSelectMenu === 'generate-template'"
                      :menu-items="generateTemplateMenuItems"
                      class="left-0 top-11 w-full text-left"
                      @action="chooseGenerateTemplate"
                    />
                  </div>
                </div>
              </div>

              <div class="grid gap-3 lg:grid-cols-3">
                <label :class="FIELD_LABEL_CLASS">
                  Nome
                  <input
                    v-model="contractForm.recipient_name"
                    :class="INPUT_CLASS"
                    placeholder="Nome completo"
                  />
                </label>
                <label :class="FIELD_LABEL_CLASS">
                  Documento
                  <select
                    v-if="selectedContactDocuments.length > 1"
                    v-model="selectedDocumentType"
                    :class="INPUT_CLASS"
                    @change="selectRecipientDocument(selectedDocumentType)"
                  >
                    <option
                      v-for="document in selectedContactDocuments"
                      :key="document.type"
                      :value="document.type"
                    >
                      {{ document.label }} · {{ document.value }}
                    </option>
                  </select>
                  <input
                    v-model="contractForm.recipient_cpf"
                    :class="INPUT_CLASS"
                    placeholder="CPF ou CNPJ"
                  />
                </label>
                <label :class="FIELD_LABEL_CLASS">
                  Telefone
                  <PhoneNumberInput
                    v-model="contractForm.recipient_phone"
                    class="w-full min-w-0 [&>div:first-child]:!h-10 [&>div:first-child]:!bg-n-solid-1 [&>div:first-child>div>div:first-child>button]:!top-0 [&>div:first-child>div>div:first-child>button]:!my-auto [&>div:first-child>div>div:first-child>button]:!ml-1 [&_input]:!h-10 [&_input]:min-w-0"
                    placeholder="Digite o telefone"
                  />
                </label>
              </div>

              <div
                class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
                      {{ copy.addressSection }}
                    </h3>
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{ copy.addressMergeHint }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="inline-flex size-8 shrink-0 items-center justify-center text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
                    :title="
                      isAddressSectionCollapsed ? 'Expandir' : 'Minimizar'
                    "
                    @click="
                      isAddressSectionCollapsed = !isAddressSectionCollapsed
                    "
                  >
                    <span
                      class="size-4"
                      :class="
                        isAddressSectionCollapsed
                          ? 'i-lucide-chevron-down'
                          : 'i-lucide-chevron-up'
                      "
                    />
                  </button>
                </div>
                <div
                  v-if="!isAddressSectionCollapsed"
                  class="flex flex-wrap gap-2"
                >
                  <button
                    type="button"
                    class="h-8 border px-3 text-xs font-semibold transition"
                    :class="
                      useContactAddress
                        ? 'border-n-brand bg-n-brand/10 text-n-brand'
                        : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                    "
                    @click="setUseContactAddress(true)"
                  >
                    {{ copy.useContactAddress }}
                  </button>
                  <button
                    type="button"
                    class="h-8 border px-3 text-xs font-semibold transition"
                    :class="
                      !useContactAddress
                        ? 'border-n-brand bg-n-brand/10 text-n-brand'
                        : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                    "
                    @click="setUseContactAddress(false)"
                  >
                    {{ copy.customContractAddress }}
                  </button>
                </div>
                <div
                  v-if="!isAddressSectionCollapsed"
                  class="grid gap-3 md:grid-cols-2"
                >
                  <label :class="FIELD_LABEL_CLASS">
                    CEP
                    <div class="flex gap-2">
                      <input
                        v-model="contractForm.recipient_zip_code"
                        :class="INPUT_CLASS"
                        :disabled="useContactAddress"
                        placeholder="00000-000"
                        @blur="lookupContractZipCode(true)"
                        @input="lookupContractZipCode()"
                      />
                      <button
                        type="button"
                        class="flex h-10 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-2 px-3 text-xs font-semibold text-n-brand shadow-sm transition hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="
                          useContactAddress ||
                          cleanZipCode(contractForm.recipient_zip_code)
                            .length !== 8 ||
                          isFetchingContractZipCode
                        "
                        :title="copy.lookupZipCode"
                        @click="lookupContractZipCode(true)"
                      >
                        <span
                          class="size-4"
                          :class="
                            isFetchingContractZipCode
                              ? 'i-lucide-loader-2 animate-spin'
                              : 'i-lucide-search'
                          "
                        />
                      </button>
                    </div>
                  </label>
                  <label :class="FIELD_LABEL_CLASS">
                    Endereço
                    <input
                      v-model="contractForm.recipient_address"
                      :class="INPUT_CLASS"
                      :disabled="useContactAddress"
                      placeholder="Rua, avenida ou logradouro"
                    />
                  </label>
                  <label :class="FIELD_LABEL_CLASS">
                    Número
                    <input
                      v-model="contractForm.recipient_address_number"
                      :class="INPUT_CLASS"
                      :disabled="useContactAddress"
                      placeholder="Número"
                    />
                  </label>
                  <label :class="FIELD_LABEL_CLASS">
                    Complemento
                    <input
                      v-model="contractForm.recipient_address_complement"
                      :class="INPUT_CLASS"
                      :disabled="useContactAddress"
                      placeholder="Apartamento, bloco, sala"
                    />
                  </label>
                  <label :class="FIELD_LABEL_CLASS">
                    Bairro
                    <input
                      v-model="contractForm.recipient_neighborhood"
                      :class="INPUT_CLASS"
                      :disabled="useContactAddress"
                      placeholder="Bairro"
                    />
                  </label>
                  <label :class="FIELD_LABEL_CLASS">
                    UF
                    <input
                      v-model="contractForm.recipient_state"
                      :class="INPUT_CLASS"
                      :disabled="useContactAddress"
                      maxlength="2"
                      placeholder="UF"
                    />
                  </label>
                  <label :class="FIELD_LABEL_CLASS">
                    Cidade
                    <input
                      v-model="contractForm.recipient_city"
                      :class="INPUT_CLASS"
                      :disabled="useContactAddress"
                      placeholder="Cidade"
                    />
                  </label>
                </div>
              </div>

              <div class="grid gap-3 lg:grid-cols-2">
                <fieldset :class="FIELD_LABEL_CLASS">
                  <legend class="mb-0">{{ copy.deliveryRange }}</legend>
                  <WootDatePicker
                    v-model:range-type="contractRangeType"
                    :date-range="contractDateTimeRange"
                    enable-time-selection
                    teleport-to-body
                    class="!w-full !min-w-0 !shrink [&>div:first-child]:!w-full [&>div:first-child]:!min-w-0 [&>div:first-child>button]:!min-w-0 [&>div:first-child>button]:!flex-1"
                    @date-range-changed="
                      updateContractDateTimeRange(
                        contractForm,
                        contractRangeType,
                        $event
                      )
                    "
                  />
                </fieldset>

                <!-- Payment method select -->
                <div :class="FIELD_LABEL_CLASS">
                  Forma de pagamento
                  <div class="flex gap-2">
                    <div
                      :ref="el => setFormSelectMenuRef('generate-payment', el)"
                      class="relative flex-1"
                    >
                      <Button
                        type="button"
                        color="slate"
                        variant="faded"
                        size="md"
                        trailing-icon
                        icon="i-lucide-chevron-down"
                        :class="[
                          SELECT_BUTTON_CLASS,
                          openFormSelectMenu === 'generate-payment'
                            ? SELECT_BUTTON_OPEN_CLASS
                            : '',
                        ]"
                        :label="generatePaymentMethodLabel"
                        @click="toggleFormSelectMenu('generate-payment')"
                      />
                      <DropdownMenu
                        v-if="openFormSelectMenu === 'generate-payment'"
                        :menu-items="generatePaymentMethodMenuItems"
                        class="left-0 top-11 w-full text-left"
                        @action="chooseGeneratePaymentMethod"
                      />
                    </div>
                    <button
                      v-tooltip.top="copy.addPaymentMethod"
                      type="button"
                      class="flex size-10 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-2 text-n-brand transition hover:bg-n-alpha-2"
                      :title="copy.addPaymentMethod"
                      @click="showAddPaymentInput = !showAddPaymentInput"
                    >
                      <span class="i-lucide-settings-2 size-4" />
                    </button>
                  </div>
                  <!-- Inline custom payment method management -->
                  <div
                    v-if="showAddPaymentInput"
                    class="mt-2 grid gap-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
                  >
                    <span class="text-xs font-semibold text-n-slate-11">
                      Gerenciar formas de pagamento
                    </span>
                    <div class="flex gap-2">
                      <input
                        v-model="newPaymentMethodInput"
                        class="flex-1"
                        :class="[INPUT_CLASS]"
                        placeholder="Nova forma de pagamento"
                        @keyup.enter="saveNewPaymentMethod"
                      />
                      <button
                        type="button"
                        class="h-10 rounded-lg bg-n-brand px-3 text-sm font-semibold text-white disabled:opacity-60"
                        :disabled="
                          !newPaymentMethodInput.trim() || isSavingPaymentMethod
                        "
                        @click="saveNewPaymentMethod"
                      >
                        Salvar
                      </button>
                    </div>
                    <div
                      v-if="customPaymentMethods.length"
                      class="flex flex-wrap gap-1.5"
                    >
                      <span
                        v-for="method in customPaymentMethods"
                        :key="method"
                        class="inline-flex items-center gap-1 rounded bg-n-slate-3 px-2 py-1 text-xs"
                      >
                        {{ method }}
                        <button
                          type="button"
                          class="text-n-ruby-11 hover:text-n-ruby-9"
                          @click="removeCustomPaymentMethod(method)"
                        >
                          <span class="i-lucide-x size-3" />
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <h3 class="text-sm font-semibold">
                  Produtos e serviços do contrato
                </h3>
                <div
                  class="grid items-end gap-3"
                  :class="
                    isCustomServiceItem
                      ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_7rem_9rem_auto]'
                      : 'lg:grid-cols-[minmax(0,1fr)_7rem_9rem_auto]'
                  "
                >
                  <div :class="FIELD_LABEL_CLASS">
                    <span>Produto/serviço</span>
                    <div
                      :ref="el => setFormSelectMenuRef('invoice-product', el)"
                      class="relative"
                    >
                      <Button
                        type="button"
                        color="slate"
                        variant="faded"
                        size="md"
                        trailing-icon
                        icon="i-lucide-chevron-down"
                        :class="[
                          SELECT_BUTTON_CLASS,
                          openFormSelectMenu === 'invoice-product'
                            ? SELECT_BUTTON_OPEN_CLASS
                            : '',
                        ]"
                        :label="productSelectLabel"
                        @click="toggleFormSelectMenu('invoice-product')"
                      />
                      <DropdownMenu
                        v-if="openFormSelectMenu === 'invoice-product'"
                        :menu-items="productMenuItems"
                        class="left-0 top-11 max-h-72 w-full overflow-y-auto text-left"
                        @action="chooseInvoiceProduct"
                      />
                    </div>
                  </div>
                  <label v-if="isCustomServiceItem" :class="FIELD_LABEL_CLASS">
                    Descrição
                    <input
                      v-model="invoiceItemForm.custom_name"
                      :class="INPUT_CLASS"
                      placeholder="Ex: Frete, comissão, montagem"
                    />
                  </label>
                  <label :class="FIELD_LABEL_CLASS">
                    Quantidade
                    <input
                      v-model="invoiceItemForm.quantity"
                      :class="INPUT_CLASS"
                      min="1"
                      type="number"
                    />
                  </label>
                  <label :class="FIELD_LABEL_CLASS">
                    Valor unitário
                    <input
                      :value="invoiceItemForm.unit_price"
                      :class="INPUT_CLASS"
                      inputmode="numeric"
                      placeholder="R$ 0,00"
                      type="text"
                      @input="maskCurrency"
                    />
                  </label>
                  <div class="mb-0 flex items-end">
                    <button
                      v-tooltip.top="copy.addProduct"
                      type="button"
                      class="flex size-10 items-center justify-center rounded-lg bg-n-brand text-white shadow-sm transition hover:bg-n-brand/90 disabled:cursor-not-allowed disabled:bg-n-slate-4 disabled:text-n-slate-10"
                      :disabled="!canAddInvoiceItem"
                      :title="copy.addProduct"
                      @click="addInvoiceItem"
                    >
                      <span class="i-lucide-plus size-4" />
                      <span class="sr-only">{{ copy.addProduct }}</span>
                    </button>
                  </div>
                </div>

                <div v-if="invoiceItems.length" class="overflow-x-auto">
                  <table class="w-full min-w-[640px] text-left text-sm">
                    <thead
                      class="border-b border-n-weak text-xs uppercase text-n-slate-10"
                    >
                      <tr>
                        <th class="py-2">Produto/serviço</th>
                        <th class="py-2 text-right">Qtd.</th>
                        <th class="py-2 text-right">Valor unitário</th>
                        <th class="py-2 text-right">Total</th>
                        <th class="py-2 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, index) in invoiceItems"
                        :key="`${item.product_type}-${item.product_id || item.product_name}-${index}`"
                        class="border-b border-n-weak last:border-b-0"
                      >
                        <td class="py-2 text-n-slate-12">
                          {{ item.product_name }}
                        </td>
                        <td class="px-2 py-2 text-right text-n-slate-12">
                          <input
                            :value="item.quantity"
                            class="ml-auto h-9 w-24 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-right text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                            min="1"
                            step="1"
                            type="number"
                            :aria-label="`Quantidade de ${item.product_name}`"
                            @input="
                              updateInvoiceItemQuantity(
                                invoiceItems,
                                index,
                                $event.target.value
                              )
                            "
                          />
                        </td>
                        <td class="px-2 py-2 text-right text-n-slate-11">
                          <input
                            :value="formatCurrency(item.unit_price)"
                            class="ml-auto h-9 w-32 rounded-lg border border-n-weak bg-n-solid-1 px-2 text-right text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                            inputmode="numeric"
                            type="text"
                            :aria-label="`Valor unitário de ${item.product_name}`"
                            @input="
                              updateInvoiceItemUnitPrice(
                                invoiceItems,
                                index,
                                $event
                              )
                            "
                          />
                        </td>
                        <td class="py-2 pl-4 text-right text-n-slate-12">
                          {{ formatCurrency(item.total) }}
                        </td>
                        <td class="py-2 text-right">
                          <button
                            v-tooltip.top="copy.delete"
                            type="button"
                            class="inline-flex size-8 items-center justify-center rounded-lg text-n-ruby-11 transition hover:bg-n-ruby-3"
                            :title="copy.delete"
                            @click="removeInvoiceItem(index)"
                          >
                            <span class="i-lucide-trash-2 size-4" />
                            <span class="sr-only">{{ copy.delete }}</span>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colspan="3" class="py-2 text-right">
                          {{ copy.invoiceTotal }}
                        </th>
                        <th class="py-2 text-right">
                          {{ formatCurrency(invoiceTotal) }}
                        </th>
                        <th />
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p v-else class="mb-0 text-xs text-n-slate-10">
                  Adicione um ou mais produtos para montar o valor final do
                  contrato.
                </p>
              </div>
            </div>
            <footer
              class="sticky bottom-0 z-10 flex flex-col gap-2 border-t border-n-weak bg-n-solid-2/95 px-5 py-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between"
            >
              <p
                v-if="selectedTemplate"
                class="mb-0 min-w-0 text-xs text-n-slate-10"
              >
                Modelo selecionado: {{ selectedTemplate.name }}
              </p>
              <span v-else />
              <div class="flex flex-wrap gap-2 sm:justify-end">
                <Button
                  type="button"
                  :label="copy.generateContract"
                  :disabled="
                    !contractForm.talkvia_contract_template_id ||
                    !contractForm.recipient_name
                  "
                  @click="generateContract"
                />
                <Button
                  type="button"
                  color="slate"
                  variant="outline"
                  :label="copy.sendToContact"
                  :disabled="
                    !contractForm.talkvia_contract_template_id ||
                    !contractForm.recipient_name ||
                    !contractForm.contact_id
                  "
                  @click="generateContract({ sendToContact: true })"
                />
                <Button
                  type="button"
                  color="slate"
                  variant="ghost"
                  :label="copy.sendQuote"
                  :disabled="
                    !contractForm.recipient_name ||
                    !contractForm.contact_id ||
                    !invoiceItems.length
                  "
                  @click="openQuoteDialog"
                />
              </div>
            </footer>
          </section>
        </div>
      </Teleport>

      <!-- Stock by date panel -->
      <section
        v-if="inventoryProducts.length"
        class="border border-n-weak bg-n-solid-2 p-4"
      >
        <div class="mb-3 flex flex-wrap items-center gap-3">
          <h2 class="text-base font-semibold">Estoque disponível por data</h2>
          <div class="flex items-center gap-2">
            <div ref="stockDatePickerRef" class="relative">
              <button
                type="button"
                class="flex h-10 w-44 items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-left text-sm text-n-slate-12 shadow-sm transition hover:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                :class="
                  showStockDatePicker
                    ? 'border-n-brand ring-1 ring-n-brand'
                    : ''
                "
                @click="openStockDatePicker"
              >
                <span class="truncate">{{ formatDateLabel(stockDate) }}</span>
                <span class="i-lucide-calendar-days size-4 shrink-0" />
              </button>

              <div
                v-if="showStockDatePicker"
                class="absolute left-0 z-50 mt-2 w-80 rounded-xl border border-n-weak bg-n-solid-2 p-3 shadow-2xl"
              >
                <div class="mb-3 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    class="grid size-8 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                    @click="shiftStockDateMonth(-1)"
                  >
                    <span class="i-lucide-chevron-left size-4" />
                  </button>
                  <h3
                    class="text-center text-sm font-semibold capitalize text-n-slate-12"
                  >
                    {{ monthTitle(stockCalendarMonth.month) }}
                  </h3>
                  <button
                    type="button"
                    class="grid size-8 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                    @click="shiftStockDateMonth(1)"
                  >
                    <span class="i-lucide-chevron-right size-4" />
                  </button>
                </div>

                <div
                  class="mb-2 grid grid-cols-7 text-center text-xs font-semibold text-n-slate-10"
                >
                  <span
                    v-for="(dayName, dayIndex) in weekDays"
                    :key="`stock-${dayIndex}`"
                    class="py-1"
                  >
                    {{ dayName }}
                  </span>
                </div>
                <div class="grid grid-cols-7 text-center text-sm">
                  <button
                    v-for="day in stockCalendarMonth.days"
                    :key="day.value"
                    type="button"
                    class="flex h-9 items-center justify-center transition"
                    :class="stockCalendarDayClass(day)"
                    @click="selectStockDate(day.value)"
                  >
                    {{ day.label }}
                  </button>
                </div>
              </div>
            </div>
            <Button
              type="button"
              label="Consultar"
              :is-loading="isLoadingStock"
              :disabled="isLoadingStock"
              @click="fetchStockByDate"
            />
          </div>
        </div>
        <div
          v-if="stockByDateResults.length"
          class="overflow-x-auto rounded-lg border border-n-weak"
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-weak bg-n-alpha-1">
                <th
                  class="px-3 py-2 text-left text-xs font-medium text-n-slate-10"
                >
                  Produto
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-n-slate-10"
                >
                  Em estoque
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-n-slate-10"
                >
                  Reservado
                </th>
                <th
                  class="px-3 py-2 text-right text-xs font-medium text-n-slate-10"
                >
                  Disponível
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in stockByDateResults"
                :key="row.product_id"
                class="border-b border-n-weak last:border-0"
                :class="
                  Number(row.available) <= 0 ? 'bg-n-ruby-3 text-n-ruby-11' : ''
                "
              >
                <td class="px-3 py-2 font-medium text-n-slate-12">
                  {{ row.product_name }}
                </td>
                <td class="px-3 py-2 text-right text-n-slate-11">
                  {{ Number(row.quantity_on_hand).toLocaleString('pt-BR') }}
                </td>
                <td class="px-3 py-2 text-right text-n-slate-11">
                  {{ Number(row.reserved).toLocaleString('pt-BR') }}
                </td>
                <td
                  class="px-3 py-2 text-right font-semibold"
                  :class="
                    Number(row.available) <= 0
                      ? 'text-n-ruby-11'
                      : 'text-n-green-11'
                  "
                >
                  {{ Number(row.available).toLocaleString('pt-BR') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="mb-0 text-xs text-n-slate-10">
          Clique em "Consultar" para ver a disponibilidade dos produtos na data
          selecionada.
        </p>
      </section>

      <!-- Contracts table -->
      <section class="border border-n-weak bg-n-solid-2 p-4">
        <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 class="text-base font-semibold">
              {{ copy.contracts }}
            </h2>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ summary.pending || 0 }} pendentes ·
              {{ summary.signed || 0 }} assinados ·
              {{ summary.awaiting_delivery || 0 }} ag. entrega ·
              {{ summary.completed || 0 }} concluídos ·
              {{ summary.expired || 0 }} expirados
            </p>
          </div>
          <div
            class="grid w-full gap-3 lg:grid-cols-[12rem_minmax(18rem,1fr)_auto_minmax(17rem,auto)_minmax(17rem,auto)] lg:items-end xl:w-auto"
          >
            <div :class="FIELD_LABEL_CLASS">
              <span>Status</span>
              <div
                v-on-clickaway="() => (showContractStatusFilter = false)"
                class="relative"
              >
                <Button
                  type="button"
                  color="slate"
                  variant="faded"
                  size="md"
                  trailing-icon
                  icon="i-lucide-chevron-down"
                  class="!h-10 w-full !justify-between !rounded-lg !border !border-n-weak !bg-n-solid-1 !px-3 !text-n-slate-12 shadow-sm"
                  :class="
                    showContractStatusFilter
                      ? '!border-n-brand !bg-n-alpha-2 ring-1 ring-n-brand'
                      : ''
                  "
                  :label="contractStatusFilterLabel"
                  @click="showContractStatusFilter = !showContractStatusFilter"
                />
                <DropdownMenu
                  v-if="showContractStatusFilter"
                  :menu-items="contractStatusFilterItems"
                  class="left-0 top-11 w-full text-left"
                  @action="applyContractStatusFilter"
                />
              </div>
            </div>
            <label :class="FIELD_LABEL_CLASS">
              Buscar
              <input
                v-model="filters.q"
                :class="INPUT_CLASS"
                :placeholder="copy.searchPlaceholder"
                @keyup.enter="fetchContracts"
              />
            </label>
            <Button
              type="button"
              label="Buscar"
              icon="i-lucide-search"
              @click="fetchContracts"
            />
            <div
              v-for="filter in dateRangeFilters"
              :key="filter.key"
              :ref="element => setDateRangeFilterRef(filter.key, element)"
              class="relative"
            >
              <span class="mb-1.5 block text-sm font-medium text-n-slate-12">
                {{ filter.label }}
              </span>
              <button
                type="button"
                class="flex h-10 w-full min-w-0 items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-left text-sm text-n-slate-12 shadow-sm transition hover:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                @click="openDateRangeFilter(filter)"
              >
                <span class="flex min-w-0 items-center gap-2">
                  <span class="i-lucide-calendar-days size-4 shrink-0" />
                  <span class="truncate">
                    {{ dateRangeButtonLabel(filter) }}
                  </span>
                </span>
                <span class="i-lucide-chevron-down size-4 shrink-0" />
              </button>

              <div
                v-if="activeDateRangeFilter === filter.key"
                class="absolute right-0 z-50 mt-2 w-[min(56rem,calc(100vw-3rem))] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
              >
                <div class="grid lg:grid-cols-[14rem_minmax(0,1fr)]">
                  <aside
                    class="border-b border-n-weak bg-n-solid-1 py-2 lg:border-b-0 lg:border-r"
                  >
                    <div
                      class="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
                    >
                      Intervalo de data
                    </div>
                    <button
                      v-for="preset in dateRangePresets"
                      :key="preset.key"
                      type="button"
                      class="flex w-full items-center px-4 py-3 text-left text-sm font-medium transition hover:bg-n-alpha-2"
                      :class="
                        dateRangeDraft.preset === preset.key
                          ? 'bg-n-alpha-3 text-n-slate-12'
                          : 'text-n-slate-11'
                      "
                      @click="setDateRangePreset(preset.key)"
                    >
                      {{ preset.label }}
                    </button>
                  </aside>

                  <div class="grid gap-4 p-4">
                    <div class="grid gap-3 md:grid-cols-2">
                      <label :class="FIELD_LABEL_CLASS">
                        Data de início
                        <div class="relative">
                          <button
                            type="button"
                            :class="DATE_CONTROL_CLASS"
                            @click="openNativeDatePicker"
                          >
                            <span class="truncate">
                              {{ formatDateLabel(dateRangeDraft.start) }}
                            </span>
                            <span
                              class="i-lucide-calendar-days size-4 shrink-0"
                            />
                          </button>
                          <input
                            v-model="dateRangeDraft.start"
                            :class="DATE_NATIVE_INPUT_CLASS"
                            type="date"
                          />
                        </div>
                      </label>
                      <label :class="FIELD_LABEL_CLASS">
                        Data final
                        <div class="relative">
                          <button
                            type="button"
                            :class="DATE_CONTROL_CLASS"
                            @click="openNativeDatePicker"
                          >
                            <span class="truncate">
                              {{ formatDateLabel(dateRangeDraft.end) }}
                            </span>
                            <span
                              class="i-lucide-calendar-days size-4 shrink-0"
                            />
                          </button>
                          <input
                            v-model="dateRangeDraft.end"
                            :class="DATE_NATIVE_INPUT_CLASS"
                            type="date"
                          />
                        </div>
                      </label>
                    </div>

                    <div class="grid gap-4 lg:grid-cols-2">
                      <section
                        v-for="(month, monthIndex) in calendarMonths"
                        :key="month.month.toISOString()"
                        class="min-w-0"
                      >
                        <div
                          class="mb-3 flex items-center justify-between gap-3"
                        >
                          <button
                            v-if="monthIndex === 0"
                            type="button"
                            class="grid size-8 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                            @click="shiftCalendarMonth(-1)"
                          >
                            <span class="i-lucide-chevron-left size-4" />
                          </button>
                          <span v-else class="size-8" />
                          <h3
                            class="text-center text-sm font-semibold capitalize text-n-slate-12"
                          >
                            {{ monthTitle(month.month) }}
                          </h3>
                          <button
                            v-if="monthIndex === 1"
                            type="button"
                            class="grid size-8 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                            @click="shiftCalendarMonth(1)"
                          >
                            <span class="i-lucide-chevron-right size-4" />
                          </button>
                          <span v-else class="size-8" />
                        </div>

                        <div
                          class="mb-2 grid grid-cols-7 text-center text-xs font-semibold text-n-slate-10"
                        >
                          <span
                            v-for="(dayName, dayIndex) in weekDays"
                            :key="`${month.month.toISOString()}-${dayIndex}`"
                            class="py-1"
                          >
                            {{ dayName }}
                          </span>
                        </div>
                        <div class="grid grid-cols-7 text-center text-sm">
                          <button
                            v-for="day in month.days"
                            :key="day.value"
                            type="button"
                            class="flex h-9 items-center justify-center transition"
                            :class="calendarDayClass(day)"
                            @click="selectDateRangeDay(day.value)"
                          >
                            {{ day.label }}
                          </button>
                        </div>
                      </section>
                    </div>

                    <footer
                      class="flex items-center justify-between border-t border-n-weak pt-4"
                    >
                      <Button
                        type="button"
                        label="Limpar"
                        color="slate"
                        variant="ghost"
                        @click="clearDateRangeFilter(filter)"
                      />
                      <Button
                        type="button"
                        label="Aplicar"
                        @click="applyDateRangeFilter(filter)"
                      />
                    </footer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="overflow-visible">
          <table class="w-full table-fixed text-left text-sm">
            <thead
              class="border-b border-n-weak text-xs uppercase text-n-slate-10"
            >
              <tr>
                <th class="w-[26%] py-2 pr-3">Cliente</th>
                <th class="w-[16%] py-2 pr-3">Modelo</th>
                <th class="w-[14%] py-2 pr-3 text-right">Valor / Pgto.</th>
                <th class="hidden w-[18%] py-2 pr-3 xl:table-cell">
                  Entrega / Retirada
                </th>
                <th class="w-[18%] py-2 pr-3">Status</th>
                <th class="w-12 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="contract in contracts"
                :key="contract.id"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="py-3 pr-3 align-top">
                  <div class="grid min-w-0 gap-1">
                    <span class="truncate font-medium text-n-slate-12">
                      {{ contract.recipient_name || '-' }}
                    </span>
                    <span class="truncate text-xs text-n-slate-10">
                      {{ contract.recipient_cpf || '-' }} ·
                      {{ contract.recipient_phone || '-' }}
                    </span>
                    <button
                      v-if="contract.conversation_id"
                      type="button"
                      class="inline-flex w-fit items-center gap-1 text-xs font-medium text-n-brand transition hover:text-n-brand/80"
                      @click="openConversation(contract)"
                    >
                      <span class="i-lucide-message-circle size-3.5" />
                      {{ copy.openConversation }}
                    </button>
                  </div>
                </td>
                <td class="py-3 pr-3 align-top">
                  <span class="line-clamp-2">
                    {{ contract.template_name || '-' }}
                  </span>
                </td>
                <td class="py-3 pr-3 text-right align-top">
                  <div class="grid min-w-0 gap-1">
                    <span class="font-medium text-n-slate-12">
                      {{
                        contract.metadata?.invoice_total != null
                          ? formatCurrency(contract.metadata.invoice_total)
                          : '-'
                      }}
                    </span>
                    <span class="truncate text-xs text-n-slate-10">
                      {{ contract.payment_method || '-' }}
                    </span>
                  </div>
                </td>
                <td class="hidden py-3 pr-3 align-top xl:table-cell">
                  <div class="grid gap-1 text-xs text-n-slate-11">
                    <span>
                      <span class="font-medium text-n-slate-12">Entrega:</span>
                      {{ formatDateTime(contract.scheduled_at) }}
                    </span>
                    <span>
                      <span class="font-medium text-n-slate-12">Retirada:</span>
                      {{ formatDateTime(contract.pickup_at) }}
                    </span>
                  </div>
                </td>
                <td class="py-3 pr-3 align-top">
                  <div class="grid gap-1.5">
                    <span
                      class="inline-flex w-fit items-center rounded-md border px-2 py-1 text-xs font-semibold"
                      :class="statusBadgeClass(contract.status)"
                    >
                      {{ contractStatusLabel(contract.status) }}
                    </span>
                    <span class="break-words text-xs text-n-slate-10">
                      Validade: {{ contract.expires_at || '-' }}
                    </span>
                    <span class="text-xs text-n-slate-10 xl:hidden">
                      Entrega: {{ formatDateTime(contract.scheduled_at) }}
                    </span>
                    <span class="text-xs text-n-slate-10 xl:hidden">
                      Retirada: {{ formatDateTime(contract.pickup_at) }}
                    </span>
                  </div>
                </td>
                <td class="py-3 text-right align-top">
                  <div
                    v-on-clickaway="closeContractActionsMenu"
                    class="relative inline-flex justify-end gap-1.5"
                  >
                    <div class="relative">
                      <Button
                        v-tooltip.top="'Alterar status'"
                        type="button"
                        color="slate"
                        variant="ghost"
                        size="sm"
                        icon="i-lucide-list-checks"
                        :is-loading="updatingStatusContractId === contract.id"
                        :class="
                          openContractStatusMenuId === contract.id
                            ? 'bg-n-alpha-2'
                            : ''
                        "
                        @click.stop="toggleContractStatusMenu(contract.id)"
                      />
                      <DropdownMenu
                        v-if="openContractStatusMenuId === contract.id"
                        :menu-items="contractStatusMenuItems(contract)"
                        class="right-0 top-9 w-52 text-left"
                        @click.stop
                        @action="handleContractAction($event, contract)"
                      />
                    </div>
                    <div class="relative">
                      <Button
                        v-tooltip.top="'Ações'"
                        type="button"
                        icon="i-lucide-ellipsis-vertical"
                        color="slate"
                        variant="ghost"
                        size="sm"
                        :class="
                          openContractActionsId === contract.id
                            ? 'bg-n-alpha-2'
                            : ''
                        "
                        @click.stop="toggleContractActionsMenu(contract.id)"
                      />
                      <DropdownMenu
                        v-if="openContractActionsId === contract.id"
                        :menu-items="contractActionMenuItems(contract)"
                        class="right-0 top-9 w-56 text-left"
                        @click.stop
                        @action="handleContractAction($event, contract)"
                      />
                    </div>
                  </div>
                </td>
              </tr>
              <tr v-if="!contracts.length && !isLoading">
                <td colspan="6" class="py-8 text-center text-n-slate-10">
                  Nenhum contrato encontrado.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>

    <ContractSendDialog
      ref="contractSendDialogRef"
      :quote-templates="quoteTemplates"
      @send="handleSendContractLink"
      @send-quote="handleSendQuote"
      @generate-quote-pdf="handleGenerateQuotePdf"
    />
    <Dialog
      ref="deleteContractDialogRef"
      type="alert"
      width="md"
      :title="copy.deleteTitle"
      :description="copy.deleteDescription"
      :cancel-button-label="copy.cancel"
      :confirm-button-label="copy.confirmDelete"
      :is-loading="isDeletingContract"
      @close="closeDeleteContractDialog"
      @confirm="confirmRemoveContract"
    >
      <p v-if="contractPendingDelete" class="mb-0 text-sm text-n-slate-11">
        {{ contractPendingDelete.recipient_name }} ·
        {{ contractPendingDelete.template_name }}
      </p>
    </Dialog>

    <!-- Contract edit modal -->
    <Teleport to="body">
      <div
        v-if="showContractEditModal"
        class="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm"
        @click.self="closeContractEditModal"
      >
        <section
          class="my-4 flex w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-2xl"
          role="dialog"
          aria-modal="true"
        >
          <header
            class="flex items-center justify-between gap-3 border-b border-n-weak px-5 py-3"
          >
            <div class="min-w-0">
              <h2 class="truncate text-lg font-semibold text-n-slate-12">
                {{ copy.editContractTitle }}
              </h2>
              <p
                v-if="editingContract"
                class="mb-0 truncate text-xs text-n-slate-10"
              >
                {{ editingContract.template_name }} ·
                {{ editingContract.recipient_name }}
              </p>
            </div>
            <button
              type="button"
              class="grid size-9 shrink-0 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="copy.close"
              @click="closeContractEditModal"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>

          <form
            class="grid gap-3 overflow-y-auto p-4"
            @submit.prevent="saveContractEdit"
          >
            <div class="grid gap-3 md:grid-cols-3">
              <label :class="EDIT_FIELD_LABEL_CLASS">
                Cliente
                <input
                  v-model="contractEditForm.recipient_name"
                  :class="EDIT_INPUT_CLASS"
                  autocomplete="name"
                  required
                />
              </label>
              <label :class="EDIT_FIELD_LABEL_CLASS">
                CPF/CNPJ
                <input
                  v-model="contractEditForm.recipient_cpf"
                  :class="EDIT_INPUT_CLASS"
                  autocomplete="off"
                />
              </label>
              <label :class="EDIT_FIELD_LABEL_CLASS">
                Telefone
                <input
                  v-model="contractEditForm.recipient_phone"
                  :class="EDIT_INPUT_CLASS"
                  autocomplete="tel"
                />
              </label>
            </div>

            <div class="grid gap-3 md:grid-cols-3">
              <div :class="EDIT_FIELD_LABEL_CLASS">
                <span>Forma de pagamento</span>
                <div
                  :ref="el => setFormSelectMenuRef('edit-payment', el)"
                  class="relative"
                >
                  <Button
                    type="button"
                    color="slate"
                    variant="faded"
                    size="md"
                    trailing-icon
                    icon="i-lucide-chevron-down"
                    :class="[
                      EDIT_SELECT_BUTTON_CLASS,
                      openFormSelectMenu === 'edit-payment'
                        ? SELECT_BUTTON_OPEN_CLASS
                        : '',
                    ]"
                    :label="editPaymentMethodLabel"
                    @click="toggleFormSelectMenu('edit-payment')"
                  />
                  <DropdownMenu
                    v-if="openFormSelectMenu === 'edit-payment'"
                    :menu-items="editPaymentMethodMenuItems"
                    class="left-0 top-11 w-full text-left"
                    @action="chooseEditPaymentMethod"
                  />
                </div>
              </div>
              <fieldset class="mb-0 grid gap-1 md:col-span-2">
                <legend class="mb-0 text-xs font-semibold text-n-slate-11">
                  Entrega / retirada
                </legend>
                <WootDatePicker
                  v-model:range-type="contractEditRangeType"
                  :date-range="contractEditDateTimeRange"
                  enable-time-selection
                  teleport-to-body
                  class="!w-full !min-w-0 !shrink [&>div:first-child]:!w-full [&>div:first-child]:!min-w-0 [&>div:first-child>button]:!min-w-0 [&>div:first-child>button]:!flex-1"
                  @date-range-changed="
                    updateContractDateTimeRange(
                      contractEditForm,
                      contractEditRangeType,
                      $event
                    )
                  "
                />
              </fieldset>
            </div>

            <div class="grid gap-3 border-t border-n-weak pt-3">
              <h3 class="text-xs font-semibold uppercase text-n-slate-10">
                Endereço do contrato
              </h3>
              <div class="grid gap-3 md:grid-cols-[0.65fr_1.9fr_0.6fr]">
                <label :class="EDIT_FIELD_LABEL_CLASS">
                  CEP
                  <div class="flex gap-2">
                    <input
                      v-model="contractEditForm.recipient_zip_code"
                      :class="EDIT_INPUT_CLASS"
                      autocomplete="postal-code"
                      placeholder="00000-000"
                      @blur="lookupContractEditZipCode(true)"
                      @input="lookupContractEditZipCode()"
                    />
                    <button
                      type="button"
                      class="flex h-9 shrink-0 items-center justify-center rounded-md border border-n-weak bg-n-solid-2 px-3 text-xs font-semibold text-n-brand shadow-sm transition hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="
                        cleanZipCode(contractEditForm.recipient_zip_code)
                          .length !== 8 || isFetchingContractEditZipCode
                      "
                      :title="copy.lookupZipCode"
                      @click="lookupContractEditZipCode(true)"
                    >
                      <span
                        class="size-4"
                        :class="
                          isFetchingContractEditZipCode
                            ? 'i-lucide-loader-2 animate-spin'
                            : 'i-lucide-search'
                        "
                      />
                    </button>
                  </div>
                </label>
                <label :class="EDIT_FIELD_LABEL_CLASS">
                  Endereço
                  <input
                    v-model="contractEditForm.recipient_address"
                    :class="EDIT_INPUT_CLASS"
                    autocomplete="street-address"
                  />
                </label>
                <label :class="EDIT_FIELD_LABEL_CLASS">
                  Número
                  <input
                    v-model="contractEditForm.recipient_address_number"
                    :class="EDIT_INPUT_CLASS"
                  />
                </label>
              </div>
              <div class="grid gap-3 md:grid-cols-[1.2fr_1fr_1fr_0.35fr]">
                <label :class="EDIT_FIELD_LABEL_CLASS">
                  Complemento
                  <input
                    v-model="contractEditForm.recipient_address_complement"
                    :class="EDIT_INPUT_CLASS"
                  />
                </label>
                <label :class="EDIT_FIELD_LABEL_CLASS">
                  Bairro
                  <input
                    v-model="contractEditForm.recipient_neighborhood"
                    :class="EDIT_INPUT_CLASS"
                  />
                </label>
                <label :class="EDIT_FIELD_LABEL_CLASS">
                  Cidade
                  <input
                    v-model="contractEditForm.recipient_city"
                    :class="EDIT_INPUT_CLASS"
                  />
                </label>
                <label :class="EDIT_FIELD_LABEL_CLASS">
                  UF
                  <input
                    v-model="contractEditForm.recipient_state"
                    :class="EDIT_INPUT_CLASS"
                    maxlength="2"
                  />
                </label>
              </div>
            </div>

            <div class="grid gap-2.5 border-t border-n-weak pt-3">
              <h3 class="text-xs font-semibold uppercase text-n-slate-10">
                Produtos e serviços do contrato
              </h3>
              <div
                class="grid items-end gap-2.5"
                :class="
                  isContractEditCustomServiceItem
                    ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_7rem_9rem_auto]'
                    : 'lg:grid-cols-[minmax(0,1fr)_7rem_9rem_auto]'
                "
              >
                <div :class="EDIT_FIELD_LABEL_CLASS">
                  <span>Produto/serviço</span>
                  <div
                    :ref="
                      el => setFormSelectMenuRef('edit-invoice-product', el)
                    "
                    class="relative"
                  >
                    <Button
                      type="button"
                      color="slate"
                      variant="faded"
                      size="md"
                      trailing-icon
                      icon="i-lucide-chevron-down"
                      :class="[
                        EDIT_SELECT_BUTTON_CLASS,
                        openFormSelectMenu === 'edit-invoice-product'
                          ? SELECT_BUTTON_OPEN_CLASS
                          : '',
                      ]"
                      :label="contractEditProductSelectLabel"
                      @click="toggleFormSelectMenu('edit-invoice-product')"
                    />
                    <DropdownMenu
                      v-if="openFormSelectMenu === 'edit-invoice-product'"
                      :menu-items="contractEditProductMenuItems"
                      class="left-0 top-11 max-h-72 w-full overflow-y-auto text-left"
                      @action="chooseContractEditInvoiceProduct"
                    />
                  </div>
                </div>
                <label
                  v-if="isContractEditCustomServiceItem"
                  :class="EDIT_FIELD_LABEL_CLASS"
                >
                  Descrição
                  <input
                    v-model="contractEditInvoiceItemForm.custom_name"
                    :class="EDIT_INPUT_CLASS"
                    placeholder="Ex: Frete, comissão, montagem"
                  />
                </label>
                <label :class="EDIT_FIELD_LABEL_CLASS">
                  Quantidade
                  <input
                    v-model="contractEditInvoiceItemForm.quantity"
                    :class="EDIT_INPUT_CLASS"
                    min="1"
                    type="number"
                  />
                </label>
                <label :class="EDIT_FIELD_LABEL_CLASS">
                  Valor unitário
                  <input
                    :value="contractEditInvoiceItemForm.unit_price"
                    :class="EDIT_INPUT_CLASS"
                    inputmode="numeric"
                    placeholder="R$ 0,00"
                    type="text"
                    @input="maskContractEditCurrency"
                  />
                </label>
                <div class="mb-0 flex items-end">
                  <button
                    v-tooltip.top="copy.addProduct"
                    type="button"
                    class="flex size-9 items-center justify-center rounded-md bg-n-brand text-white shadow-sm transition hover:bg-n-brand/90 disabled:cursor-not-allowed disabled:bg-n-slate-4 disabled:text-n-slate-10"
                    :disabled="!canAddContractEditInvoiceItem"
                    :title="copy.addProduct"
                    @click="addContractEditInvoiceItem"
                  >
                    <span class="i-lucide-plus size-4" />
                    <span class="sr-only">{{ copy.addProduct }}</span>
                  </button>
                </div>
              </div>

              <div
                v-if="contractEditInvoiceItems.length"
                class="overflow-x-auto"
              >
                <table class="w-full min-w-[640px] text-left text-xs">
                  <thead
                    class="border-b border-n-weak text-[11px] uppercase text-n-slate-10"
                  >
                    <tr>
                      <th class="py-1.5">Produto/serviço</th>
                      <th class="py-1.5 text-right">Qtd.</th>
                      <th class="py-1.5 text-right">Valor unitário</th>
                      <th class="py-1.5 text-right">Total</th>
                      <th class="py-1.5 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in contractEditInvoiceItems"
                      :key="`${item.product_type}-${item.product_id || item.product_name}-${index}`"
                      class="border-b border-n-weak last:border-b-0"
                    >
                      <td class="py-1.5 text-n-slate-12">
                        {{ item.product_name }}
                      </td>
                      <td class="px-2 py-1.5 text-right text-n-slate-12">
                        <input
                          :value="item.quantity"
                          class="ml-auto h-8 w-20 rounded-md border border-n-weak bg-n-solid-1 px-2 text-right text-xs text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                          min="1"
                          step="1"
                          type="number"
                          :aria-label="`Quantidade de ${item.product_name}`"
                          @input="
                            updateInvoiceItemQuantity(
                              contractEditInvoiceItems,
                              index,
                              $event.target.value
                            )
                          "
                        />
                      </td>
                      <td class="px-2 py-1.5 text-right text-n-slate-11">
                        <input
                          :value="formatCurrency(item.unit_price)"
                          class="ml-auto h-8 w-28 rounded-md border border-n-weak bg-n-solid-1 px-2 text-right text-xs text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand"
                          inputmode="numeric"
                          type="text"
                          :aria-label="`Valor unitário de ${item.product_name}`"
                          @input="
                            updateInvoiceItemUnitPrice(
                              contractEditInvoiceItems,
                              index,
                              $event
                            )
                          "
                        />
                      </td>
                      <td class="py-1.5 pl-4 text-right text-n-slate-12">
                        {{ formatCurrency(item.total) }}
                      </td>
                      <td class="py-1.5 text-right">
                        <button
                          v-tooltip.top="copy.delete"
                          type="button"
                          class="inline-flex size-8 items-center justify-center rounded-lg text-n-ruby-11 transition hover:bg-n-ruby-3"
                          :title="copy.delete"
                          @click="removeContractEditInvoiceItem(index)"
                        >
                          <span class="i-lucide-trash-2 size-4" />
                          <span class="sr-only">{{ copy.delete }}</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colspan="3" class="py-1.5 text-right">
                        {{ copy.invoiceTotal }}
                      </th>
                      <th class="py-1.5 text-right">
                        {{ formatCurrency(contractEditInvoiceTotal) }}
                      </th>
                      <th />
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p v-else class="mb-0 text-xs text-n-slate-10">
                Adicione um ou mais produtos para montar o valor final do
                contrato.
              </p>
            </div>

            <footer
              class="flex flex-col-reverse gap-2 border-t border-n-weak pt-3 sm:flex-row sm:justify-end"
            >
              <button
                type="button"
                class="h-9 border border-n-weak px-3 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
                :disabled="isSavingContractEdit"
                @click="closeContractEditModal"
              >
                {{ copy.cancel }}
              </button>
              <button
                type="submit"
                class="h-9 bg-n-brand px-3 text-sm font-semibold text-white disabled:opacity-60"
                :disabled="isSavingContractEdit"
              >
                {{ copy.saveContract }}
              </button>
            </footer>
          </form>
        </section>
      </div>
    </Teleport>

    <!-- Template modal -->
    <Teleport to="body">
      <div
        v-if="showTemplateModal"
        class="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/55 p-4 backdrop-blur-sm"
        @click.self="showTemplateModal = false"
      >
        <section
          class="my-8 flex w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
          role="dialog"
          aria-modal="true"
        >
          <header
            class="flex items-center justify-between gap-4 border-b border-n-weak px-6 py-4"
          >
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ copy.manageTemplates }}
            </h2>
            <button
              type="button"
              class="grid size-9 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="copy.close"
              @click="showTemplateModal = false"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>

          <div class="grid min-h-0 gap-0 lg:grid-cols-[280px_minmax(0,1fr)]">
            <!-- Template list -->
            <aside
              class="border-b border-n-weak bg-n-solid-1 p-4 lg:border-b-0 lg:border-r"
            >
              <div class="mb-3 flex items-center justify-between">
                <span class="text-sm font-semibold text-n-slate-11">
                  Modelos criados
                </span>
                <button
                  v-tooltip.top="copy.newTemplate"
                  type="button"
                  class="inline-flex size-8 items-center justify-center text-n-brand transition hover:bg-n-alpha-2"
                  :title="copy.newTemplate"
                  @click="resetTemplateForm(templateModalKind)"
                >
                  <span class="i-lucide-file-plus-2 size-4" />
                </button>
              </div>
              <div
                class="mb-3 grid grid-cols-2 gap-1 rounded-lg bg-n-alpha-1 p-1 text-xs font-semibold"
              >
                <button
                  type="button"
                  :class="
                    templateModalKind === 'contract'
                      ? 'bg-n-solid-2 text-n-brand shadow-sm'
                      : 'text-n-slate-10'
                  "
                  class="rounded-md px-2 py-1.5"
                  @click="
                    templateModalKind = 'contract';
                    resetTemplateForm('contract');
                  "
                >
                  {{ copy.contractTemplates }}
                </button>
                <button
                  type="button"
                  :class="
                    templateModalKind === 'quote'
                      ? 'bg-n-solid-2 text-n-brand shadow-sm'
                      : 'text-n-slate-10'
                  "
                  class="rounded-md px-2 py-1.5"
                  @click="
                    templateModalKind = 'quote';
                    resetTemplateForm('quote');
                  "
                >
                  {{ copy.quoteTemplates }}
                </button>
              </div>
              <div class="grid gap-1">
                <div
                  v-for="template in visibleTemplates"
                  :key="template.id"
                  class="flex items-center justify-between rounded-lg px-2 py-2 hover:bg-n-alpha-2"
                >
                  <button
                    type="button"
                    class="min-w-0 flex-1 text-left"
                    @click="openTemplateModal(template)"
                  >
                    <span
                      class="block truncate text-sm font-medium text-n-slate-12"
                    >
                      {{ template.name }}
                    </span>
                    <span class="block truncate text-xs text-n-slate-10">
                      {{
                        templateKind(template) === 'quote'
                          ? 'Modelo de orçamento'
                          : `${template.contracts_count || 0} contratos`
                      }}
                    </span>
                  </button>
                  <button
                    type="button"
                    class="flex size-7 shrink-0 items-center justify-center text-n-ruby-11 hover:bg-n-ruby-3"
                    :title="copy.delete"
                    @click="removeTemplate(template)"
                  >
                    <span class="i-lucide-trash-2 size-3.5" />
                  </button>
                </div>
                <p
                  v-if="!visibleTemplates.length"
                  class="px-2 text-xs text-n-slate-10"
                >
                  Nenhum modelo criado.
                </p>
              </div>
            </aside>

            <!-- Template form -->
            <div class="grid gap-4 overflow-y-auto p-5">
              <div class="grid gap-3">
                <label class="grid gap-1.5 text-sm font-medium text-n-slate-12">
                  Nome do modelo
                  <input
                    v-model="templateForm.name"
                    :class="INPUT_CLASS"
                    :placeholder="
                      templateModalKind === 'quote'
                        ? 'Ex: Orçamento padrão de locação'
                        : 'Ex: Contrato de adesão'
                    "
                  />
                </label>
                <div class="grid gap-2">
                  <span class="text-xs font-medium text-n-slate-11">
                    Parâmetros
                  </span>
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="parameter in PARAMETER_OPTIONS"
                      :key="parameter"
                      type="button"
                      class="border border-n-weak bg-n-alpha-1 px-2 py-1 text-xs font-medium text-n-brand hover:border-n-brand hover:bg-n-alpha-2"
                      @mousedown.prevent="insertParameter(parameter)"
                    >
                      {{ parameter }}
                    </button>
                  </div>
                </div>
                <div data-contract-editor>
                  <Editor
                    v-model="templateForm.body"
                    editor-key="talkvia-contract-template-editor"
                    :placeholder="
                      templateModalKind === 'quote'
                        ? 'Modelo de orçamento'
                        : 'Contrato'
                    "
                    class="[&_.ProseMirror-woot-style]:!min-h-[22rem] [&_.ProseMirror-woot-style]:!max-h-[44rem]"
                    :show-character-count="false"
                    :max-length="0"
                    override-line-breaks
                    enable-variables
                  />
                </div>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ copy.bodyHint }}
                </p>
                <div
                  v-if="templateModalKind === 'contract'"
                  class="grid gap-2 border border-n-weak bg-n-alpha-1 p-3"
                >
                  <span class="text-sm font-semibold">
                    {{ copy.requiredDocuments }}
                  </span>
                  <div class="grid gap-2">
                    <label
                      v-for="documentName in DEFAULT_DOCUMENTS"
                      :key="documentName"
                      class="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        :checked="requiredDocuments.includes(documentName)"
                        @change="toggleRequiredDocument(documentName)"
                      />
                      {{ documentName }}
                    </label>
                  </div>
                  <div class="flex gap-2">
                    <label class="min-w-0 flex-1">
                      <span class="sr-only">{{ copy.customDocument }}</span>
                      <input
                        v-model="customDocumentName"
                        :class="INPUT_CLASS"
                        :placeholder="copy.customDocument"
                        @keyup.enter="addCustomDocument"
                      />
                    </label>
                    <button
                      type="button"
                      class="h-9 bg-n-slate-3 px-3 text-sm"
                      @click="addCustomDocument"
                    >
                      Adicionar
                    </button>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="documentName in requiredDocuments.filter(
                        item => !DEFAULT_DOCUMENTS.includes(item)
                      )"
                      :key="documentName"
                      type="button"
                      class="bg-n-slate-3 px-2 py-1 text-xs text-n-slate-12"
                      @click="toggleRequiredDocument(documentName)"
                    >
                      {{ documentName }} x
                    </button>
                  </div>
                </div>
                <label class="flex items-center gap-2 text-sm">
                  <input v-model="templateForm.active" type="checkbox" />
                  Modelo ativo
                </label>
                <label
                  v-if="templateModalKind === 'contract'"
                  class="flex items-center gap-2 text-sm"
                >
                  <input
                    v-model="templateForm.require_selfie"
                    type="checkbox"
                  />
                  Exigir selfie ao assinar
                </label>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="h-10 bg-n-brand px-4 text-sm font-semibold text-white disabled:opacity-60"
                    :disabled="!templateForm.name || !templateForm.body"
                    @click="saveTemplate"
                  >
                    {{
                      templateForm.id ? copy.updateTemplate : copy.saveTemplate
                    }}
                  </button>
                  <button
                    v-if="templateForm.id"
                    type="button"
                    class="h-10 border border-n-weak px-4 text-sm text-n-slate-11 hover:bg-n-alpha-2"
                    @click="resetTemplateForm"
                  >
                    Novo modelo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Teleport>

    <!-- Dashboard modal -->
    <Teleport to="body">
      <div
        v-if="showDashboard"
        class="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-md"
        @click.self="showDashboard = false"
      >
        <section
          class="w-full overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl transition-all duration-200"
          :class="
            isDashboardExpanded
              ? 'my-2 max-w-[calc(100vw-2rem)]'
              : 'my-8 max-w-5xl'
          "
          role="dialog"
          aria-modal="true"
        >
          <header class="border-b border-n-weak px-6 py-4">
            <div class="mb-3 flex items-start justify-between gap-4">
              <div>
                <h2 class="text-lg font-semibold text-n-slate-12">
                  Dashboard de Contratos
                </h2>
                <p class="mb-0 text-xs text-n-slate-10">
                  <template v-if="isDashboardLoading">Carregando...</template>
                  <template v-else>
                    {{ dashboardMetrics.total }} contratos · valor total
                    {{ formatCurrency(dashboardMetrics.totalValue) }}
                  </template>
                </p>
              </div>
              <div class="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  class="grid size-9 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :aria-label="
                    isDashboardExpanded ? 'Reduzir modal' : 'Expandir modal'
                  "
                  :title="
                    isDashboardExpanded ? 'Reduzir modal' : 'Expandir modal'
                  "
                  @click="isDashboardExpanded = !isDashboardExpanded"
                >
                  <span
                    class="size-5"
                    :class="
                      isDashboardExpanded
                        ? 'i-lucide-minimize-2'
                        : 'i-lucide-maximize-2'
                    "
                  />
                </button>
                <button
                  type="button"
                  class="grid size-9 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :aria-label="copy.close"
                  @click="showDashboard = false"
                >
                  <span class="i-lucide-x size-5" />
                </button>
              </div>
            </div>

            <!-- Date range filter -->
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-xs font-medium text-n-slate-10">Período:</span>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="h-7 rounded-lg px-3 text-xs font-medium transition"
                  :class="
                    !dashboardDateRange.start && !dashboardDateRange.end
                      ? 'bg-n-brand text-white'
                      : 'border border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  @click="setDashboardPreset('all')"
                >
                  Tudo
                </button>
                <button
                  type="button"
                  class="h-7 rounded-lg border border-n-weak px-3 text-xs font-medium text-n-slate-11 transition hover:bg-n-alpha-2"
                  @click="setDashboardPreset('month')"
                >
                  Este mês
                </button>
                <button
                  type="button"
                  class="h-7 rounded-lg border border-n-weak px-3 text-xs font-medium text-n-slate-11 transition hover:bg-n-alpha-2"
                  @click="setDashboardPreset('30d')"
                >
                  30 dias
                </button>
                <button
                  type="button"
                  class="h-7 rounded-lg border border-n-weak px-3 text-xs font-medium text-n-slate-11 transition hover:bg-n-alpha-2"
                  @click="setDashboardPreset('year')"
                >
                  Este ano
                </button>
              </div>
              <div class="flex items-center gap-1.5">
                <div class="relative">
                  <button
                    type="button"
                    :class="`${DATE_CONTROL_CLASS} h-7 px-2 text-xs`"
                    @click="openNativeDatePicker"
                  >
                    <span class="truncate">
                      {{ formatDateLabel(dashboardDateRange.start) }}
                    </span>
                    <span class="i-lucide-calendar-days size-3.5 shrink-0" />
                  </button>
                  <input
                    v-model="dashboardDateRange.start"
                    type="date"
                    :class="DATE_NATIVE_INPUT_CLASS"
                  />
                </div>
                <span class="text-xs text-n-slate-10">até</span>
                <div class="relative">
                  <button
                    type="button"
                    :class="`${DATE_CONTROL_CLASS} h-7 px-2 text-xs`"
                    @click="openNativeDatePicker"
                  >
                    <span class="truncate">
                      {{ formatDateLabel(dashboardDateRange.end) }}
                    </span>
                    <span class="i-lucide-calendar-days size-3.5 shrink-0" />
                  </button>
                  <input
                    v-model="dashboardDateRange.end"
                    type="date"
                    :class="DATE_NATIVE_INPUT_CLASS"
                  />
                </div>
                <button
                  type="button"
                  class="h-7 rounded-lg bg-n-brand px-3 text-xs font-semibold text-white transition hover:brightness-95 disabled:opacity-60"
                  :disabled="isDashboardLoading"
                  @click="applyDashboardDateRange"
                >
                  <span
                    v-if="isDashboardLoading"
                    class="i-lucide-loader-circle size-3.5 animate-spin"
                  />
                  <span v-else>Aplicar</span>
                </button>
              </div>
            </div>
          </header>

          <div
            v-if="isDashboardLoading"
            class="flex items-center justify-center py-16"
          >
            <span
              class="i-lucide-loader-circle size-8 animate-spin text-n-brand"
            />
          </div>

          <div v-else class="grid gap-5 p-6">
            <!-- Status cards -->
            <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <div
                v-for="s in dashboardMetrics.byStatus"
                :key="s.value"
                class="grid gap-1 rounded-xl border p-4"
                :class="
                  statusBadgeClass(s.value)
                    .replace('text-', 'border-')
                    .split(' ')[0] + ' bg-n-solid-1'
                "
              >
                <span class="text-2xl font-bold text-n-slate-12">
                  {{ s.count }}
                </span>
                <span class="text-xs text-n-slate-10">{{ s.label }}</span>
                <div class="mt-1 h-1.5 w-full rounded-full bg-n-alpha-2">
                  <div
                    class="h-1.5 rounded-full bg-current transition-all"
                    :class="
                      statusBadgeClass(s.value)
                        .split(' ')
                        .find(c => c.startsWith('text-'))
                    "
                    :style="{
                      width: dashboardMetrics.total
                        ? `${Math.round((s.count / dashboardMetrics.total) * 100)}%`
                        : '0%',
                    }"
                  />
                </div>
              </div>
            </div>

            <!-- Delivery & value highlights -->
            <div class="grid gap-3 sm:grid-cols-3">
              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <p class="mb-1 text-xs text-n-slate-10">Valor total</p>
                <p class="mb-0 text-xl font-bold text-n-slate-12">
                  {{ formatCurrency(dashboardMetrics.totalValue) }}
                </p>
              </div>
              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <p class="mb-1 text-xs text-n-slate-10">Com data de entrega</p>
                <p class="mb-0 text-xl font-bold text-n-slate-12">
                  {{ dashboardMetrics.withDelivery }}
                </p>
              </div>
              <div
                class="rounded-xl border p-4"
                :class="
                  dashboardMetrics.overdue > 0
                    ? 'border-n-ruby-5 bg-n-ruby-3'
                    : 'border-n-weak bg-n-solid-1'
                "
              >
                <p class="mb-1 text-xs text-n-slate-10">Entregas atrasadas</p>
                <p
                  class="mb-0 text-xl font-bold"
                  :class="
                    dashboardMetrics.overdue > 0
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{ dashboardMetrics.overdue }}
                </p>
              </div>
            </div>

            <div class="grid gap-5 lg:grid-cols-2">
              <!-- By template -->
              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
                  Por modelo
                </h3>
                <div class="grid gap-2">
                  <div
                    v-for="t in dashboardMetrics.byTemplate"
                    :key="t.name"
                    class="flex items-center gap-3"
                  >
                    <span
                      class="min-w-0 flex-1 truncate text-sm text-n-slate-11"
                    >
                      {{ t.name }}
                    </span>
                    <div class="flex items-center gap-2">
                      <div class="h-1.5 w-20 rounded-full bg-n-alpha-2">
                        <div
                          class="h-1.5 rounded-full bg-n-brand transition-all"
                          :style="{
                            width: dashboardMetrics.total
                              ? `${Math.round((t.count / dashboardMetrics.total) * 100)}%`
                              : '0%',
                          }"
                        />
                      </div>
                      <span
                        class="w-6 text-right text-xs font-semibold text-n-slate-12"
                      >
                        {{ t.count }}
                      </span>
                    </div>
                  </div>
                  <p
                    v-if="!dashboardMetrics.byTemplate.length"
                    class="text-xs text-n-slate-10"
                  >
                    Nenhum dado disponível.
                  </p>
                </div>
              </div>

              <!-- By payment method -->
              <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
                  Por forma de pagamento
                </h3>
                <div class="grid gap-2">
                  <div
                    v-for="p in dashboardMetrics.byPayment"
                    :key="p.name"
                    class="flex items-center gap-3"
                  >
                    <span
                      class="min-w-0 flex-1 truncate text-sm text-n-slate-11"
                    >
                      {{ p.name }}
                    </span>
                    <div class="flex items-center gap-2">
                      <div class="h-1.5 w-20 rounded-full bg-n-alpha-2">
                        <div
                          class="h-1.5 rounded-full bg-n-teal-9 transition-all"
                          :style="{
                            width: dashboardMetrics.total
                              ? `${Math.round((p.count / dashboardMetrics.total) * 100)}%`
                              : '0%',
                          }"
                        />
                      </div>
                      <span
                        class="w-6 text-right text-xs font-semibold text-n-slate-12"
                      >
                        {{ p.count }}
                      </span>
                    </div>
                  </div>
                  <p
                    v-if="!dashboardMetrics.byPayment.length"
                    class="text-xs text-n-slate-10"
                  >
                    Nenhum dado disponível.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="showDocumentsModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
        @click.self="showDocumentsModal = false"
      >
        <section
          class="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
          role="dialog"
          aria-modal="true"
        >
          <header
            class="flex items-center justify-between gap-4 border-b border-n-weak px-6 py-4"
          >
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ copy.documentsTitle }}
              </h2>
              <p class="mt-0.5 text-xs text-n-slate-10">
                {{ selectedDocumentsName }}
              </p>
            </div>
            <button
              type="button"
              class="grid size-9 place-content-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              @click="showDocumentsModal = false"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </header>
          <div class="p-4">
            <p
              v-if="!selectedDocuments.length"
              class="py-6 text-center text-sm text-n-slate-10"
            >
              {{ copy.noDocuments }}
            </p>
            <ul v-else class="flex flex-col gap-2">
              <li
                v-for="(doc, index) in selectedDocuments"
                :key="index"
                class="flex items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3"
              >
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium text-n-slate-12">
                    {{ doc.label || doc.file_name }}
                  </p>
                  <p class="truncate text-xs text-n-slate-10">
                    {{ doc.file_name }}
                  </p>
                </div>
                <a
                  :href="documentDownloadHref(doc)"
                  :download="doc.file_name"
                  class="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-n-brand/90"
                >
                  <span class="i-lucide-download size-3.5" />
                  {{ copy.downloadDocument }}
                </a>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </Teleport>
  </main>
</template>
